import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Box, Paper, Typography, Grid, Switch, TextField, FormControlLabel, Checkbox,
  Button, Divider, Alert, CircularProgress, Card, CardContent, FormGroup, Chip,
  Accordion, AccordionSummary, AccordionDetails, Tooltip, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SyncIcon from '@mui/icons-material/Sync';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import MicIcon from '@mui/icons-material/Mic';
import RefreshIcon from '@mui/icons-material/Refresh';
import HostedAIAvailableModels from '../../components/HostedAI/HostedAIAvailableModels';
import HostedAISectionRouting from '../../components/HostedAI/HostedAISectionRouting';

// Tip Management feature keys for special handling
const tipManagementFeatureKeys = ['tipManagement', 'shiftManagement', 'shiftCloseout', 'tipDistribution', 'postCloseAdjustments'];

// Supported business types — matches BusinessType Swift enum raw values
const ALL_BUSINESS_TYPES = [
  { value: 'jewelry',           label: 'Jewelry' },
  { value: 'watches',           label: 'Watches' },
  { value: 'electronics',       label: 'Electronics' },
  { value: 'grocery',           label: 'Grocery' },
  { value: 'automotive',        label: 'Car Dealership' },
  { value: 'clothing',          label: 'Clothing' },
  { value: 'restaurant',        label: 'Restaurant' },
  { value: 'coffee_shop',       label: 'Coffee Shop' },
  { value: 'wine_cellar',       label: 'Wine & Cellar' },
  { value: 'hardware',          label: 'Hardware' },
  { value: 'bookstore',         label: 'Bookstore' },
  { value: 'hair_beauty_salon', label: 'Hair & Beauty Salon' },
  { value: 'aesthetics_clinic', label: 'Aesthetics Clinic' },
  { value: 'gemstone',          label: 'Gemstone' },
  { value: 'real_estate',       label: 'Real Estate' },
  { value: 'general',           label: 'General' },
];

// Tax API Provider definitions for tier availability control
function mergeDeepHosted(base, patch) {
  if (!patch || typeof patch !== 'object') return { ...base };
  const out = { ...base };
  for (const k of Object.keys(patch)) {
    const pv = patch[k];
    if (pv === undefined) continue;
    const bv = out[k];
    if (pv && typeof pv === 'object' && !Array.isArray(pv) && bv && typeof bv === 'object' && !Array.isArray(bv)) {
      out[k] = mergeDeepHosted(bv, pv);
    } else {
      out[k] = pv;
    }
  }
  return out;
}

/** Mirrors website-template `HOSTED_OPERATION_TYPES` / cost-registry labels for admin editing. */
const HOSTED_OPERATION_ROWS = [
  { key: 'alt_text', label: 'Alt text' },
  { key: 'product_meta', label: 'Product meta' },
  { key: 'product_description_short', label: 'Short product description' },
  { key: 'product_description_long', label: 'Long product description' },
  { key: 'page_seo', label: 'Page SEO' },
  { key: 'seo_audit_item_fix', label: 'SEO audit fix' },
  { key: 'blog_draft', label: 'Blog draft' },
  { key: 'newsletter_draft', label: 'Newsletter draft' },
  { key: 'analytics_summary', label: 'Analytics summary' },
  { key: 'ad_copy_variant', label: 'Ad copy variant' },
  { key: 'voice_command_parse', label: 'Voice command parse' },
  { key: 'voice_command_clarify', label: 'Voice command clarify' },
  { key: 'setup_consultant', label: 'Setup consultant' },
  { key: 'setup_voice_advisor', label: 'Setup voice advisor' },
  { key: 'account_setup', label: 'Account setup extraction' },
];

/**
 * Mirrors website-template `DEFAULT_OPERATION_COSTS` from
 * `website-template/src/lib/ai/cost-registry.ts`. Used **only** for placeholder
 * hints in the admin form so empty cells show the default that will actually
 * apply at runtime. The website resolves the real value via
 * `getEffectiveOperationCost`, which falls back to the same registry defaults
 * when an override is missing — keep these two tables in sync.
 */
const HOSTED_OPERATION_DEFAULTS = {
  alt_text:                  { creditWeight: 1, maxInputTokens: 1500, maxOutputTokens: 80,   allowedInBulk: true,  perDayCapPerCompany: null, perWeekCapPerCompany: null },
  product_meta:              { creditWeight: 1, maxInputTokens: 2000, maxOutputTokens: 240,  allowedInBulk: true,  perDayCapPerCompany: null, perWeekCapPerCompany: null },
  product_description_short: { creditWeight: 1, maxInputTokens: 2000, maxOutputTokens: 300,  allowedInBulk: true,  perDayCapPerCompany: null, perWeekCapPerCompany: null },
  product_description_long:  { creditWeight: 3, maxInputTokens: 3000, maxOutputTokens: 800,  allowedInBulk: true,  perDayCapPerCompany: null, perWeekCapPerCompany: null },
  page_seo:                  { creditWeight: 1, maxInputTokens: 2000, maxOutputTokens: 240,  allowedInBulk: true,  perDayCapPerCompany: null, perWeekCapPerCompany: null },
  seo_audit_item_fix:        { creditWeight: 1, maxInputTokens: 2000, maxOutputTokens: 300,  allowedInBulk: true,  perDayCapPerCompany: null, perWeekCapPerCompany: null },
  blog_draft:                { creditWeight: 5, maxInputTokens: 3000, maxOutputTokens: 2000, allowedInBulk: true,  perDayCapPerCompany: 1,    perWeekCapPerCompany: 1 },
  newsletter_draft:          { creditWeight: 5, maxInputTokens: 3000, maxOutputTokens: 2000, allowedInBulk: true,  perDayCapPerCompany: 1,    perWeekCapPerCompany: 1 },
  analytics_summary:         { creditWeight: 2, maxInputTokens: 4000, maxOutputTokens: 600,  allowedInBulk: false, perDayCapPerCompany: null, perWeekCapPerCompany: null },
  ad_copy_variant:           { creditWeight: 1, maxInputTokens: 1500, maxOutputTokens: 240,  allowedInBulk: true,  perDayCapPerCompany: null, perWeekCapPerCompany: null },
  voice_command_parse:         { creditWeight: 1, maxInputTokens: 3000, maxOutputTokens: 500,  allowedInBulk: false, perDayCapPerCompany: null, perWeekCapPerCompany: null },
  voice_command_clarify:       { creditWeight: 1, maxInputTokens: 2500, maxOutputTokens: 250,  allowedInBulk: false, perDayCapPerCompany: null, perWeekCapPerCompany: null },
  page_editor_enhance:        { creditWeight: 3, maxInputTokens: 32000, maxOutputTokens: 2000, allowedInBulk: false, perDayCapPerCompany: null, perWeekCapPerCompany: null },
  page_editor_organize:       { creditWeight: 8, maxInputTokens: 48000, maxOutputTokens: 5000, allowedInBulk: false, perDayCapPerCompany: null, perWeekCapPerCompany: null },
  page_editor_generate_block: { creditWeight: 5, maxInputTokens: 32000, maxOutputTokens: 4000, allowedInBulk: false, perDayCapPerCompany: null, perWeekCapPerCompany: null },
  page_editor_seo_fix:        { creditWeight: 4, maxInputTokens: 32000, maxOutputTokens: 2000, allowedInBulk: false, perDayCapPerCompany: null, perWeekCapPerCompany: null },
  page_editor_bulk_seo:       { creditWeight: 15, maxInputTokens: 48000, maxOutputTokens: 8000, allowedInBulk: true,  perDayCapPerCompany: null, perWeekCapPerCompany: null },
  page_editor_template_style: { creditWeight: 6, maxInputTokens: 32000, maxOutputTokens: 2000, allowedInBulk: false, perDayCapPerCompany: null, perWeekCapPerCompany: null },
  page_editor_layout_optimize:{ creditWeight: 8, maxInputTokens: 48000, maxOutputTokens: 5000, allowedInBulk: false, perDayCapPerCompany: null, perWeekCapPerCompany: null },
  page_editor_generate_image: { creditWeight: 4, maxInputTokens: 3000, maxOutputTokens: 500,  allowedInBulk: false, perDayCapPerCompany: null, perWeekCapPerCompany: null },
  setup_consultant:           { creditWeight: 2, maxInputTokens: 8000, maxOutputTokens: 4096, allowedInBulk: false, perDayCapPerCompany: 8,    perWeekCapPerCompany: 5 },
  setup_voice_advisor:        { creditWeight: 1, maxInputTokens: 4000, maxOutputTokens: 500,  allowedInBulk: false, perDayCapPerCompany: 40,   perWeekCapPerCompany: 80 },
  account_setup:              { creditWeight: 1, maxInputTokens: 4000, maxOutputTokens: 1000, allowedInBulk: false, perDayCapPerCompany: 20,   perWeekCapPerCompany: 40 },
};

const placeholderForDefault = (v) => (v == null ? '—' : String(v));

function defaultHostedAIPlatform() {
  return {
    enabled: true,
    customerAIEnabled: true,
    setupConsultantHostedGrantEnabled: true,
    hostedProvider: 'gemini_flash',
    hostedProviderFailoverEnabled: true,
    operationsBlockedFromHosted: [],
    monthlyReset: 'UTC_CALENDAR_MONTH',
    weeklyReset: 'UTC_ISO_WEEK_MONDAY',
    byokFallback: {
      preferBYOKWhenConfigured: true,
      autoFallbackOnHostedExhaustion: true,
    },
    abuseControls: {
      defaultDailyPromptLimit: null,
      perCompanyRequestsPerMinute: 20,
      perUserRequestsPerMinute: null,
      maxConcurrentRequestsPerCompany: 3,
      // Global safety ceiling for the small, high-volume hosted operations
      // (alt text, product meta, SEO, blog, voice, etc.). Advanced Editor
      // (page_editor_*) operations are NOT clamped by this — they use their own
      // generous per-operation maxInputTokens (see cost-registry.ts /
      // getEffectiveOperationCost). Keep in sync with website-template DEFAULT_CONFIG.
      maxInputTokens: 64000,
      maxOutputTokens: 8000,
      maxRequestBodyBytes: 1048576,
      maxImagePixels: 4000000,
      monthlyProviderSpendCapCents: null,
    },
    bulkLimits: {
      maxItemsPerJob: 500,
      maxBulkJobsPerDay: 10,
      minSecondsBetweenBulkJobs: 60,
      requirePreflightConfirmation: true,
      maxConcurrentBulkJobsPerCompany: 1,
    },
    deduplication: { enabled: true, ttlSeconds: 604800 },
    operationCosts: {},
    tiers: {
      free: {
        hostedAIEnabled: false,
        byokEnabled: true,
        monthlyPromptLimit: 0,
        dailyPromptLimit: 0,
        bulkOperationsEnabled: false,
        maxItemsPerBulkJob: 0,
      },
      standard: {
        hostedAIEnabled: false,
        byokEnabled: true,
        monthlyPromptLimit: 0,
        dailyPromptLimit: 0,
        bulkOperationsEnabled: false,
        maxItemsPerBulkJob: 0,
      },
      professional: {
        hostedAIEnabled: true,
        byokEnabled: true,
        monthlyPromptLimit: 200,
        dailyPromptLimit: 50,
        bulkOperationsEnabled: true,
        maxItemsPerBulkJob: 200,
      },
      enterprise: {
        hostedAIEnabled: true,
        byokEnabled: true,
        monthlyPromptLimit: 800,
        dailyPromptLimit: 200,
        bulkOperationsEnabled: true,
        maxItemsPerBulkJob: 500,
      },
    },
    usageNotifications: { warnAtPercent: [50, 75, 90, 100], notifyChannels: ['app_banner'] },
    providerApiKeysConfigured: { openai: false, gemini: false, claude: false, deepseek: false, kimi: false, glm: false },
    providerApiKeysStatus: { openai: 'not_set', gemini: 'not_set', claude: 'not_set', deepseek: 'not_set', kimi: 'not_set', glm: 'not_set' },
    providerApiKeysUnreadable: { openai: false, gemini: false, claude: false, deepseek: false, kimi: false, glm: false },
    enabledModels: { openai: [], gemini: [], claude: [], deepseek: [], kimi: [], glm: [] },
  };
}

/**
 * Merge server patch with defaults and coerce shapes so UI never calls .join on non-arrays.
 * Bad legacy documents can store strings/objects and would crash the Features & Limits view.
 */
function normalizeHostedAIPlatform(patch) {
  const base = defaultHostedAIPlatform();
  const raw =
    patch && typeof patch === 'object' && !Array.isArray(patch) ? patch : {};
  const merged = mergeDeepHosted(base, raw);

  let warnAt = merged.usageNotifications?.warnAtPercent;
  if (!Array.isArray(warnAt)) {
    warnAt = base.usageNotifications.warnAtPercent;
  }
  warnAt = warnAt.map((n) => parseInt(String(n), 10)).filter((n) => !Number.isNaN(n));
  if (warnAt.length === 0) warnAt = [...base.usageNotifications.warnAtPercent];

  let channels = merged.usageNotifications?.notifyChannels;
  if (!Array.isArray(channels)) {
    channels = [...base.usageNotifications.notifyChannels];
  }
  channels = channels.map((c) => String(c).trim()).filter(Boolean);
  if (channels.length === 0) channels = ['app_banner'];

  let blocked = merged.operationsBlockedFromHosted;
  if (!Array.isArray(blocked)) {
    if (typeof blocked === 'string' && blocked.trim()) {
      blocked = blocked.split(',').map((s) => s.trim()).filter(Boolean);
    } else {
      blocked = [];
    }
  }

  let opCosts = merged.operationCosts;
  if (opCosts == null || typeof opCosts !== 'object' || Array.isArray(opCosts)) {
    opCosts = {};
  }

  const pkc = merged.providerApiKeysConfigured;
  const providerApiKeysConfigured = {
    openai: pkc && typeof pkc === 'object' && pkc.openai === true,
    gemini: pkc && typeof pkc === 'object' && pkc.gemini === true,
    claude: pkc && typeof pkc === 'object' && pkc.claude === true,
    deepseek: pkc && typeof pkc === 'object' && pkc.deepseek === true,
    kimi: pkc && typeof pkc === 'object' && pkc.kimi === true,
    glm: pkc && typeof pkc === 'object' && pkc.glm === true,
  };
  const pku = merged.providerApiKeysUnreadable;
  const providerApiKeysUnreadable = {
    openai: pku && typeof pku === 'object' && pku.openai === true,
    gemini: pku && typeof pku === 'object' && pku.gemini === true,
    claude: pku && typeof pku === 'object' && pku.claude === true,
    deepseek: pku && typeof pku === 'object' && pku.deepseek === true,
    kimi: pku && typeof pku === 'object' && pku.kimi === true,
    glm: pku && typeof pku === 'object' && pku.glm === true,
  };
  const providerApiKeysStatus = {
    openai: providerApiKeysUnreadable.openai
      ? 'action_required'
      : providerApiKeysConfigured.openai ? 'saved' : 'not_set',
    gemini: providerApiKeysUnreadable.gemini
      ? 'action_required'
      : providerApiKeysConfigured.gemini ? 'saved' : 'not_set',
    claude: providerApiKeysUnreadable.claude
      ? 'action_required'
      : providerApiKeysConfigured.claude ? 'saved' : 'not_set',
    deepseek: providerApiKeysUnreadable.deepseek
      ? 'action_required'
      : providerApiKeysConfigured.deepseek ? 'saved' : 'not_set',
    kimi: providerApiKeysUnreadable.kimi
      ? 'action_required'
      : providerApiKeysConfigured.kimi ? 'saved' : 'not_set',
    glm: providerApiKeysUnreadable.glm
      ? 'action_required'
      : providerApiKeysConfigured.glm ? 'saved' : 'not_set',
  };

  const em = merged.enabledModels;
  const enabledModels = {
    openai: Array.isArray(em?.openai) ? em.openai.map((id) => String(id).trim()).filter(Boolean) : [],
    gemini: Array.isArray(em?.gemini) ? em.gemini.map((id) => String(id).trim()).filter(Boolean) : [],
    claude: Array.isArray(em?.claude) ? em.claude.map((id) => String(id).trim()).filter(Boolean) : [],
    deepseek: Array.isArray(em?.deepseek) ? em.deepseek.map((id) => String(id).trim()).filter(Boolean) : [],
    kimi: Array.isArray(em?.kimi) ? em.kimi.map((id) => String(id).trim()).filter(Boolean) : [],
    glm: Array.isArray(em?.glm) ? em.glm.map((id) => String(id).trim()).filter(Boolean) : [],
  };

  return {
    ...merged,
    providerApiKeysConfigured,
    providerApiKeysStatus,
    providerApiKeysUnreadable,
    enabledModels,
    usageNotifications: {
      ...base.usageNotifications,
      ...merged.usageNotifications,
      warnAtPercent: warnAt,
      notifyChannels: channels,
    },
    operationsBlockedFromHosted: blocked,
    operationCosts: opCosts,
  };
}

const DEFAULT_FEATURE_FLAGS = {
  enableMultiCompany: true,
  enableQuickCreate: false,
  quickCreatePilotTenants: [],
  quickCreatePilotBusinessTypes: [],
  enableVisualQuickAdd: true,
  visualQuickAddPilotTenants: [],
  visualQuickAddPilotBusinessTypes: [],
  enableAIMarketing: false,
  enableNovoMarket: true,
  enableSetupConsultant: true,
  setupConsultantPilotTenants: [],
  setupConsultantPilotBusinessTypes: [],
  enabledLanguages: [],
};

function normalizeListFlag(value) {
  if (Array.isArray(value)) return value.map(v => String(v).trim()).filter(Boolean);
  if (typeof value === 'string') return value.split(',').map(v => v.trim()).filter(Boolean);
  return [];
}

function normalizeFeatureFlags(patch = {}) {
  const raw = patch && typeof patch === 'object' && !Array.isArray(patch) ? patch : {};
  return {
    ...DEFAULT_FEATURE_FLAGS,
    ...raw,
    enableMultiCompany: raw.enableMultiCompany !== false,
    enableQuickCreate: raw.enableQuickCreate === true,
    quickCreatePilotTenants: normalizeListFlag(raw.quickCreatePilotTenants),
    quickCreatePilotBusinessTypes: normalizeListFlag(raw.quickCreatePilotBusinessTypes),
    enableVisualQuickAdd: raw.enableVisualQuickAdd === true,
    visualQuickAddPilotTenants: normalizeListFlag(raw.visualQuickAddPilotTenants),
    visualQuickAddPilotBusinessTypes: normalizeListFlag(raw.visualQuickAddPilotBusinessTypes),
    enableAIMarketing: raw.enableAIMarketing === true,
    enableNovoMarket: raw.enableNovoMarket !== false,
    enableSetupConsultant: raw.enableSetupConsultant !== false,
    setupConsultantPilotTenants: normalizeListFlag(raw.setupConsultantPilotTenants),
    setupConsultantPilotBusinessTypes: normalizeListFlag(raw.setupConsultantPilotBusinessTypes),
    enabledLanguages: normalizeListFlag(raw.enabledLanguages),
  };
}

const taxAPIProviderDefinitions = {
  taxjar: {
    name: 'TaxJar',
    description: 'Automated sales tax calculation, reporting, and filing',
    icon: '🧾',
    defaultTiers: ['professional', 'enterprise']
  },
  avalara: {
    name: 'Avalara',
    description: 'Enterprise-grade tax compliance and automation platform',
    icon: '🏢',
    defaultTiers: ['enterprise']
  },
  stripe: {
    name: 'Stripe Tax',
    description: 'Automatic tax calculation via Stripe Connect (OAuth-based)',
    icon: '💳',
    defaultTiers: ['standard', 'professional', 'enterprise']
  }
};

// Novo Voice sub-capabilities — synced when the parent voiceAssistant toggle changes.
// voiceFeatureSetup is intentionally NOT in this list: it is an independent kill
// switch and must never cascade on from Novo Voice.
const voiceAssistantSubFeatureKeys = [
  'voiceAssistantBulkActions',
  'voiceAssistantInvoices',
  'voiceAssistantUsers',
  'siriHeadlessIntents',
  'siriWriteIntents',
  'siriQuickStart',
  'voiceAssistantMcp',
  'voiceAssistantMcpPageRead',
  'voiceAssistantMcpPageDraft',
  'voiceAssistantMcpPageGenerate',
  'voiceAssistantMcpPagePreview',
  'voiceAssistantMcpPageVisualAnalysis',
  'voiceAssistantMcpPageSiteWide',
  'voiceAssistantMcpPageMedia',
  'voiceAssistantMcpResources',
  'voiceAssistantMcpImageGen',
];

// AI children that cascade from the single aiFeatures toggle. The portal exposes
// one AI toggle (aiFeatures); these are the individual kill switches the iOS app
// gates on, and each follows the parent unless an admin explicitly flips it.
const aiFeaturesSubFeatureKeys = [
  'imagePlaygroundInventory',
  'foundationModelsProfiles',
];

const VOICE_FEATURE_SETUP_DEFAULT = {
  enabled: false,
  price: 0,
  availableInTiers: ['professional', 'enterprise'],
  minimumTier: 'professional',
};

// Features that depend on Website Access being enabled for their tier
const websiteAccessDependentFeatureKeys = [
  'websiteBuilder',     // Website Builder requires website access
  'onlineStore',        // Online Store requires a website
  'advancedCart',       // Advanced Cart requires online store/website
  'scheduledDrops',     // Scheduled Drops requires online store/website
  'flashSales',         // Flash Sales requires online store/website
  'onlineBookingAppointments', // Online Booking requires a website
  'whatsappWebsiteChat',      // WhatsApp Website Chat requires a website
  'liveChatWidget',            // Live Chat Widget requires a website
  'emailManagement',           // Email Management requires website backend for sending
  'customDomainEmail',         // Custom Domain Email requires website backend for credential storage
  'customDomain',              // Custom Domain requires a website; ticks stay empty until an admin selects tiers
  'b2bFeatures',               // B2B Features require website for customer registration, pricing, and sync
  'marketingAutomation',       // Marketing Automation requires website backend for trigger execution
  'campaignABTesting',         // Campaign A/B Testing requires email management + website backend
  'websiteABTesting',          // Website A/B Testing requires website to serve experiment variants
];

const SIRI_WIDGETS_APPLE_INTELLIGENCE_CATEGORY = 'Siri, Widgets & Apple Intelligence';

// Feature definitions organized by category
const featureCategories = {
  [SIRI_WIDGETS_APPLE_INTELLIGENCE_CATEGORY]: {
    homeWidgets: { name: 'Home Screen & Lock Screen Widgets', description: 'Home Screen and Lock Screen widgets showing sales, appointments, low stock, and overdue invoices' },
    siriHeadlessIntents: { name: 'Siri Headless Reads', description: 'Siri answers sales, inventory and appointments without opening Novo' },
    siriWriteIntents: { name: 'Siri Headless Writes', description: 'Siri creates customers, appointments, drafts and more with a confirmation' },
    siriQuickStart: { name: 'Siri Quick Start', description: 'Siri asks the first setup questions in its own voice, then opens Novo pre-filled' },
    imagePlaygroundInventory: { name: 'Apple Image Playground (Inventory Photos)', description: 'Turn inventory photos into catalog-style images with Apple Image Playground' },
    foundationModelsProfiles: { name: 'Apple Foundation Models', description: 'On-device / Private Cloud Compute AI for Siri profiles, guided commands and vision' },
  },
  'Core': {
    coreAppFeatures: { name: 'Core App Features', description: 'Essential business management' }
  },
  'E-Commerce': {
    onlineStore: { name: 'Online Store', description: 'E-commerce functionality' },
    advancedCart: { name: 'Advanced Cart', description: 'Advanced shopping cart features' },
    scheduledDrops: { name: 'Scheduled Drops', description: 'Schedule product releases' },
    flashSales: { name: 'Flash Sales', description: 'Time-limited sales events' }
  },
  'Website': {
    websiteBuilder: { name: 'Website Builder', description: 'Drag-and-drop website builder' },
    websiteAccess: { name: 'Website Access', description: 'Public-facing website' },
    customDomain: { name: 'Custom Domain', description: 'Connect your own domain to your Novo website. Existing connected domains keep working if a tier is later turned off.' }
  },
  'AI & Automation': {
    aiSetupConsultant: { name: 'AI Setup Consultant', description: 'Interview during Simple/Advanced setup and Settings. Hosted AI for the interview is the Interview checkbox above.' },
    aiFeatures: { name: 'AI Ad Generation', description: 'AI-powered content creation' },
    aiAnalytics: { name: 'AI Analytics', description: 'AI-powered business analytics' },
    voiceAssistant: { name: 'Novo Voice (Voice AI)', description: 'In-app voice commands via Hi Novo — disabled by default until rollout' },
    voiceFeatureSetup: { name: 'Novo Voice Feature Setup', description: 'Conversational setup that configures features after confirmation. Off by default and never cascades from Novo Voice.' },
    voiceAccountSetup: { name: 'Novo Voice Account Setup', description: 'Voice during account creation (first-run). On by default for every plan so a new owner can create the account by voice; this toggle is the kill switch.' },
    voiceSurfacesDebugOnly: { name: 'Novo Voice Debug Only', description: 'On: Novo Voice and account voice setup show only in debug builds. Off: they show in debug and release builds when their own switches are on.' },
    voiceAssistantMcp: { name: 'Novo Voice Desktop Connectors (Claude / ChatGPT MCP)', description: 'Connect Claude Desktop and ChatGPT to your Novo business data via MCP. Users can query sales, inventory, appointments, and more from their desktop AI assistants.' },
    voiceAssistantMcpPageRead: { name: 'MCP Website Page Reads', description: 'Let ChatGPT/Claude read website page structure, SEO, brand context, and templates via MCP.' },
    voiceAssistantMcpPageDraft: { name: 'MCP Website Page Drafts', description: 'Let external assistants create and update Advanced Editor page drafts (Apply + Publish still required in Novo).' },
    voiceAssistantMcpPageGenerate: { name: 'MCP AI Page Layout Generate', description: 'Hosted AI layout generation for MCP (generate_page_layout_draft).' },
    voiceAssistantMcpPagePreview: { name: 'MCP Page Preview & Inspect', description: 'Draft preview URLs, live page summary, draft-vs-live compare, and text render analysis.' },
    voiceAssistantMcpPageVisualAnalysis: { name: 'MCP Visual Page Analysis', description: 'Optional pixel-level page analysis via AI vision (analyze_page_render_visual).' },
    voiceAssistantMcpPageSiteWide: { name: 'MCP Site-Wide Drafts', description: 'Coordinated multi-page design drafts via MCP (generate_site_wide_draft).' },
    voiceAssistantMcpPageMedia: { name: 'MCP Media Library', description: 'List company media and attach assets to page drafts from ChatGPT/Claude.' },
    voiceAssistantMcpResources: { name: 'MCP Resources Protocol', description: 'Expose novo:// page and brand resources via MCP resources/list and resources/read.' },
    voiceAssistantMcpImageGen: { name: 'MCP Block Image Generation', description: 'AI image generation for page blocks when an image provider is configured.' },
    aiDesignPage: { name: 'AI Design Page', description: 'Design entire pages with AI — describe what you want and get professional layouts' },
    aiDesignSite: { name: 'AI Design Website', description: 'Design your entire website with consistent navigation, branding, and visitor flow across all pages' },
    aiFreeformSiteDesign: { name: 'AI Freeform Site Design', description: 'Your selected model designs the whole website via the Apps API — not Novo editor tools' },
    aiMultiVariant: { name: 'AI Multi-Variant', description: 'Generate multiple design variants and choose your favorite layout' },
    aiStyleTokens: { name: 'AI Style Tokens', description: 'Apply consistent design styles and themes across your website' },
    aiCompetitiveAnalyze: { name: 'AI Competitive Analyze', description: 'Get AI-powered competitive analysis and design recommendations' },
    aiBrandPersonality: { name: 'AI Brand Personality', description: 'Define and apply your brand personality to AI-generated designs' },
    aiRefine: { name: 'AI Refine', description: 'Iteratively refine AI designs with targeted improvement prompts' },
    aiSiteCohesion: { name: 'AI Site Cohesion', description: 'Ensure visual consistency across your entire website' }
  },
  'Appointments': {
    onlineBookingAppointments: { name: 'Online Booking', description: 'Appointment scheduling' },
    calendarManagement: { name: 'Calendar Management', description: 'Advanced calendar features' }
  },
  'Shipping': {
    shippingManagement: { name: 'Shipping Management', description: 'Shipping and tracking' }
  },
  'Payments & Billing': {
    paymentSystemIntegration: { name: 'Payment Integration', description: 'Payment gateways (Stripe, Square, etc.)' },
    advancedInvoicing: { name: 'Advanced Invoicing', description: 'Advanced invoice features' }
  },
  'Communication': {
    chatSystem: { name: 'Chat System', description: 'Team and customer chat' },
    emailManagement: { name: 'Email Management', description: 'Email templates and automation' },
    supportInbox: { name: 'Support Inbox', description: 'Customer support inbox in iOS Settings (Gmail / Outlook OAuth / IMAP)' },
    customDomainEmail: { name: 'Custom Domain Email', description: 'Allow users to send transactional emails from their own domain (e.g. noreply@theirbusiness.com) using Resend or another provider.' },
    whatsappServiceIntegration: { name: 'WhatsApp Integration', description: 'WhatsApp Business messaging for invoices, estimates, reminders' },
    whatsappWebsiteChat: { name: 'WhatsApp Website Chat', description: 'Allow website visitors to contact via WhatsApp' },
    liveChatWidget: { name: 'Live Chat Widget', description: 'Crisp live chat widget on website' }
  },
  'Multi-Store': {
    multiLocationSupport: { name: 'Multi-Location', description: 'Multiple store locations' },
    inventoryTransfers: { name: 'Inventory Transfers', description: 'Transfer stock between locations' }
  },
  'Analytics': {
    advancedReports: { name: 'Advanced Reports', description: 'Comprehensive analytics' }
  },
  'Team': {
    managedStaffInvites: {
      name: 'Managed Staff Invites',
      description: 'Invite employees as managed users so they can log in from their own devices with role-based access controls'
    }
  },
  'Advanced Features': {
    quickCreate: { name: 'Quick Create', description: 'Business-aware streamlined inventory/catalog item creation' },
    visualQuickAdd: { name: 'Visual Quick-Add', description: 'POS visual grid, dashboard Quick-Add tab, and visual New Invoice entry mode' },
    customFields: { name: 'Custom Fields', description: 'Custom data fields for products/customers' },
    consignmentOut: { name: 'Consignment Out', description: 'Consignment inventory tracking' },
    returnsManagement: { name: 'Returns Management', description: 'Process and track returns' },
    billsManagement: { name: 'Bills Management', description: 'Manage vendor bills and expenses' },
    commissionTracking: { name: 'Commission Tracking', description: 'Track staff commissions automatically' }
  },
  'Integrations': {
    zapierIntegration: { name: 'Zapier Integration', description: 'Connect with 5000+ apps' },
    apiAccess: { name: 'API Access', description: 'REST API access for developers' }
  },
  'Financial Tools': {
    taxLiabilityReport: { name: 'Tax Liability Report', description: 'Generate tax liability reports' },
    taxExemptionReport: { name: 'Tax Exemption Report', description: 'Track tax exemptions' },
    taxAuditTrail: { name: 'Tax Audit Trail', description: 'Complete tax audit history' },
    taxNexusTracking: { name: 'Tax Nexus Tracking', description: 'Track sales tax nexus by state' },
    taxAPIProvider: { name: 'Tax API Provider', description: 'TaxJar, Avalara integration' },
    taxRateScheduling: { name: 'Tax Rate Scheduling', description: 'Schedule future tax rate changes' },
    taxAuditLog: { name: 'Tax Audit Log', description: 'Detailed tax change logging' },
    taxReviewReminders: { name: 'Tax Review Reminders', description: 'Automatic tax review alerts' },
    accountingExport: { name: 'Accounting Export', description: 'Export to QuickBooks, Xero' }
  },
  'B2B & Wholesale': {
    b2bFeatures: { name: 'B2B Features', description: 'Business-to-business sales, wholesale pricing, customer approval workflows' }
  },
  'Restaurant': {
    restaurantPOS: { name: 'Restaurant POS', description: 'Restaurant point-of-sale functionality' },
    tableManagement: { name: 'Table Management', description: 'Restaurant table management and reservations' }
  },
  'CRM & Growth': {
    marketingAutomation: { name: 'Marketing Automation', description: 'Automated triggers, drip workflows, and lifecycle campaigns (abandoned cart, win-back, welcome series)' },
    campaignABTesting:   { name: 'Campaign A/B Testing', description: 'Multi-variant subject line and content testing for email campaigns' },
    websiteABTesting:    { name: 'Website A/B Testing',  description: 'Experiment on hero sections, CTAs, and page layouts with statistical significance' },
  },
  'Customer Engagement': {
    loyaltyProgram: { name: 'Loyalty Program', description: 'Customer loyalty points and rewards' }
  },
  'Social Media': {
    socialMediaIntegration: { name: 'Social Media Integration', description: 'Connect and post to social media platforms' },
    socialMediaFacebook: { name: 'Facebook', description: 'Post products and updates to Facebook' },
    socialMediaInstagram: { name: 'Instagram', description: 'Share products and stories on Instagram' },
    socialMediaTwitter: { name: 'Twitter/X', description: 'Publish posts, images, and videos to X (Twitter)' },
    socialMediaTikTok: { name: 'TikTok', description: 'Create and share product videos on TikTok' }
  },
  'Repair & Service': {
    repairService: { name: 'Repair Service', description: 'Manage repair jobs with intake, quotes, and pickup workflow' }
  },
  'Client Intelligence': {
    clientIntelligence: { name: 'Client Intelligence & Recommendations', description: 'On-device purchase profiles, due/at-risk clients, suggested items and potential buyers for an item, in the app and in Novo Voice. Off by default; the app fails closed until this is enabled.' }
  },
  'Tip Management & Shifts': {
    tipManagement: { name: 'Tip Management', description: 'Overall tip management system including tip policies and role buckets' },
    shiftManagement: { name: 'Shift Management', description: 'Create and manage work shifts with employee assignments' },
    shiftCloseout: { name: 'Shift Closeout', description: 'Shift closeout functionality for finalizing tips and generating reports' },
    tipDistribution: { name: 'Tip Distribution', description: 'Tip distribution modes (direct tips vs pooled tips) configuration' },
    postCloseAdjustments: { name: 'Post-Close Adjustments', description: 'Post-closeout adjustments for refunds and voids' }
  }
};

// Flatten features for easy access
const features = Object.entries(featureCategories).reduce((acc, [category, feats]) => {
  Object.entries(feats).forEach(([key, info]) => {
    acc[key] = { ...info, category };
  });
  return acc;
}, {});

const ALL_TIERS = ['free', 'standard', 'professional', 'enterprise'];
const TIER_NEUTRAL_FEATURE_KEYS = new Set([
  'quickCreate',
  'visualQuickAdd',
  'aiSetupConsultant',
  // Account-creation voice must work on every plan (it runs before a company
  // exists), so it is tier-neutral rather than paid-tier gated.
  'voiceAccountSetup',
  'voiceSurfacesDebugOnly',
  // Appointments & scheduling are available on every plan (Free included).
  'onlineBookingAppointments',
  'calendarManagement',
]);

export default function FeaturesLimitsContent() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [featurePricing, setFeaturePricing] = useState({});
  const [tierPricing, setTierPricing] = useState({});
  const [featureFlags, setFeatureFlags] = useState(() => normalizeFeatureFlags());
  // Global toggle for tip management - synced with Tip Management tab
  const [tipManagementGlobalEnabled, setTipManagementGlobalEnabled] = useState(true);
  // Tax API Provider availability per tier
  const [taxProviderAvailability, setTaxProviderAvailability] = useState(() => {
    const defaults = {};
    Object.entries(taxAPIProviderDefinitions).forEach(([key, def]) => {
      defaults[key] = { enabled: true, availableInTiers: [...def.defaultTiers] };
    });
    return defaults;
  });
  const [hostedAIPlatform, setHostedAIPlatform] = useState(() => normalizeHostedAIPlatform(null));
  /** Collapsed by default to keep Features & Limits scannable on initial load. */
  const [hostedAiSectionExpanded, setHostedAiSectionExpanded] = useState(false);
  const [customerAiSectionExpanded, setCustomerAiSectionExpanded] = useState(false);
  const [hostedProviderKeyOpenai, setHostedProviderKeyOpenai] = useState('');
  const [hostedProviderKeyGemini, setHostedProviderKeyGemini] = useState('');
  const [hostedProviderKeyClaude, setHostedProviderKeyClaude] = useState('');
  const [hostedProviderKeyDeepseek, setHostedProviderKeyDeepseek] = useState('');
  const [hostedProviderKeyKimi, setHostedProviderKeyKimi] = useState('');
  const [hostedProviderKeyGlm, setHostedProviderKeyGlm] = useState('');
  const [clearHostedProviderOpenai, setClearHostedProviderOpenai] = useState(false);
  const [clearHostedProviderGemini, setClearHostedProviderGemini] = useState(false);
  const [clearHostedProviderClaude, setClearHostedProviderClaude] = useState(false);
  const [clearHostedProviderDeepseek, setClearHostedProviderDeepseek] = useState(false);
  const [clearHostedProviderKimi, setClearHostedProviderKimi] = useState(false);
  const [clearHostedProviderGlm, setClearHostedProviderGlm] = useState(false);
  const [providerKeyTest, setProviderKeyTest] = useState({
    openai: { testing: false, result: null },
    gemini: { testing: false, result: null },
    claude: { testing: false, result: null },
    deepseek: { testing: false, result: null },
    kimi: { testing: false, result: null },
    glm: { testing: false, result: null },
  });
  const [hostedCatalogRefreshToken, setHostedCatalogRefreshToken] = useState(0);

  /**
   * Test a provider key against the actual provider via the BI backend.
   * Uses the unsaved typed value if present, otherwise asks the server to test
   * the currently-encrypted stored key (the key itself never leaves the server
   * in that path).
   */
  const handleTestProviderKey = async (provider) => {
    const typedByProvider = {
      openai: hostedProviderKeyOpenai.trim(),
      gemini: hostedProviderKeyGemini.trim(),
      claude: hostedProviderKeyClaude.trim(),
      deepseek: hostedProviderKeyDeepseek.trim(),
      kimi: hostedProviderKeyKimi.trim(),
      glm: hostedProviderKeyGlm.trim(),
    };
    const typed = typedByProvider[provider] || '';
    const hasStored = !!hostedAIPlatform.providerApiKeysConfigured?.[provider];
    const hasUnreadableStoredKey = !!hostedAIPlatform.providerApiKeysUnreadable?.[provider];

    if (!typed && hasUnreadableStoredKey) {
      setProviderKeyTest((prev) => ({
        ...prev,
        [provider]: {
          testing: false,
          result: {
            valid: false,
            code: 'DECRYPTION_FAILED',
            message: 'Action Required: Re-save key. The stored key metadata exists, but the encrypted key cannot be read.',
          },
        },
      }));
      return;
    }

    if (!typed && !hasStored) {
      setProviderKeyTest((prev) => ({
        ...prev,
        [provider]: {
          testing: false,
          result: { valid: false, message: 'Enter a key above before testing.' },
        },
      }));
      return;
    }

    setProviderKeyTest((prev) => ({
      ...prev,
      [provider]: { testing: true, result: null },
    }));

    try {
      const body = typed
        ? { provider, key: typed }
        : { provider, useStored: true };
      const resp = await fetch('/api/config/feature-pricing/test-provider-key', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await resp.json();
      const valid = data?.success === true && data?.valid === true;
      const message = data?.message
        || data?.error
        || (valid ? 'Key is valid' : 'Key validation failed');
      if (data?.code === 'DECRYPTION_FAILED') {
        setHostedAIPlatform((prev) => normalizeHostedAIPlatform({
          ...prev,
          providerApiKeysConfigured: {
            ...prev.providerApiKeysConfigured,
            [provider]: false,
          },
          providerApiKeysUnreadable: {
            ...prev.providerApiKeysUnreadable,
            [provider]: true,
          },
        }));
      }
      setProviderKeyTest((prev) => ({
        ...prev,
        [provider]: {
          testing: false,
          result: { valid, message, model: data?.model, code: data?.code },
        },
      }));
      if (valid) {
        setHostedCatalogRefreshToken((token) => token + 1);
      }
    } catch (err) {
      setProviderKeyTest((prev) => ({
        ...prev,
        [provider]: {
          testing: false,
          result: { valid: false, message: `Network error: ${err.message}` },
        },
      }));
    }
  };

  useEffect(() => { fetchConfiguration(); }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const h = (window.location.hash || '').replace(/^#/, '');
    if (h === 'bi-hosted-ai-api-keys') {
      setHostedAiSectionExpanded(true);
    }
    if (h === 'bi-customer-ai') {
      setCustomerAiSectionExpanded(true);
    }
  }, []);

  const fetchConfiguration = async () => {
    try {
      setLoading(true);
      const [pricingResp, flagsResp] = await Promise.all([
        fetch('/api/config/feature-pricing'),
        fetch('/api/config/feature-flags')
      ]);

      const data = await pricingResp.json();
      const flagsData = await flagsResp.json();

      if (flagsData?.success) {
        setFeatureFlags(normalizeFeatureFlags(flagsData.featureFlags));
      }

      if (data.success) {
        const initialized = {};
        for (const [key] of Object.entries(features)) {
          const existingConfig = data.featurePricing?.[key];
          if (existingConfig) {
            // Use existing config but ensure availableInTiers matches enabled state
            // If enabled=true but no tiers selected, default to all tiers
            const shouldHaveAllTiers = existingConfig.enabled &&
              key !== 'customDomain' &&
              (!existingConfig.availableInTiers || existingConfig.availableInTiers.length === 0);
            const isTierNeutralFeature = TIER_NEUTRAL_FEATURE_KEYS.has(key);
            initialized[key] = {
              ...existingConfig,
              availableInTiers: isTierNeutralFeature && existingConfig.enabled
                ? [...ALL_TIERS]
                : shouldHaveAllTiers ? [...ALL_TIERS] : (existingConfig.availableInTiers || [])
            };
          } else {
            // New feature defaults — voiceAssistant stays off until explicitly enabled
            if (key === 'voiceAssistant') {
              initialized[key] = { enabled: false, price: 0, availableInTiers: ['enterprise'] };
            } else if (key === 'voiceSurfacesDebugOnly') {
              initialized[key] = { enabled: true, price: 0, availableInTiers: [...ALL_TIERS] };
            } else if (key === 'voiceFeatureSetup') {
              initialized[key] = { ...VOICE_FEATURE_SETUP_DEFAULT };
            } else if (key === 'voiceAssistantMcp') {
              initialized[key] = { enabled: false, price: 0, availableInTiers: ['enterprise'] };
            } else if (key === 'managedStaffInvites') {
              // Matches backend fallback + initialize-feature-pricing defaults
              initialized[key] = { enabled: true, price: 0, availableInTiers: ['professional', 'enterprise'] };
            } else if (key === 'homeWidgets') {
              // Matches backend fallback + initialize-feature-pricing defaults
              initialized[key] = { enabled: true, price: 0, availableInTiers: ['professional', 'enterprise'] };
            } else if (key.startsWith('voiceAssistantMcpPage')) {
              initialized[key] = {
                enabled: false,
                price: 0,
                availableInTiers: key === 'voiceAssistantMcpPageRead'
                  ? ['free', 'standard', 'professional', 'enterprise']
                  : key === 'voiceAssistantMcpPageSiteWide'
                    ? ['enterprise']
                    : ['professional', 'enterprise'],
                minimumTier: key === 'voiceAssistantMcpPageRead'
                  ? 'free'
                  : key === 'voiceAssistantMcpPageSiteWide'
                    ? 'enterprise'
                    : 'professional',
              };
            } else if (key === 'siriHeadlessIntents' || key === 'siriWriteIntents' || key === 'siriQuickStart') {
              // Child of voiceAssistant: match the parent's current state so a
              // pre-save render never shows (or persists) a state that would break
              // Siri gating — Siri must stay off whenever Novo Voice is off.
              const voiceOn = data.featurePricing?.voiceAssistant?.enabled === true;
              initialized[key] = { enabled: voiceOn, price: 0, availableInTiers: voiceOn ? ['enterprise'] : [] };
            } else if (key === 'imagePlaygroundInventory' || key === 'foundationModelsProfiles') {
              // Child of aiFeatures: match the parent so an existing account keeps
              // working and a fresh account stays off until AI is turned on.
              const aiOn = data.featurePricing?.aiFeatures?.enabled === true;
              initialized[key] = { enabled: aiOn, price: 0, availableInTiers: aiOn ? ['professional', 'enterprise'] : [] };
            } else if (key === 'customDomain') {
              // Ships locked. An empty tier list is the locked state. Do not
              // fill every tier on first load; the owner ticks tiers later.
              initialized[key] = { enabled: true, price: 0, availableInTiers: [] };
            } else {
              initialized[key] = { enabled: true, price: 0, availableInTiers: [...ALL_TIERS] };
            }
          }
        }
        setFeaturePricing(initialized);
        setTierPricing(data.tierPricing || {});
        setHostedAIPlatform(
          normalizeHostedAIPlatform(data.featurePricing?.hostedAIPlatform)
        );
        
        // Load the global tip management toggle state
        // If not explicitly set, derive from whether any tip feature is enabled
        if (data.featurePricing?._tipManagementGlobalEnabled !== undefined) {
          setTipManagementGlobalEnabled(data.featurePricing._tipManagementGlobalEnabled);
        } else {
          const anyTipFeatureEnabled = tipManagementFeatureKeys.some(key => 
            data.featurePricing?.[key]?.enabled
          );
          setTipManagementGlobalEnabled(anyTipFeatureEnabled);
        }

        // Load tax provider availability per tier
        const savedTaxAvailability = data.featurePricing?._taxProviderAvailability;
        if (savedTaxAvailability) {
          const loaded = {};
          Object.entries(taxAPIProviderDefinitions).forEach(([key, def]) => {
            loaded[key] = {
              enabled: savedTaxAvailability[key]?.enabled ?? true,
              availableInTiers: savedTaxAvailability[key]?.availableInTiers ?? [...def.defaultTiers]
            };
          });
          setTaxProviderAvailability(loaded);
        }
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFeatureToggle = (key, field, value) => {
    setFeaturePricing(prev => {
      const updated = { ...prev[key], [field]: value };
      // When enabling a feature, default all tiers to checked
      // When disabling a feature, clear all tier selections
      // voiceFeatureSetup is independent: enabling it never unlocks Free/Standard
      if (field === 'enabled') {
        if (key === 'voiceFeatureSetup') {
          updated.availableInTiers = value ? [...VOICE_FEATURE_SETUP_DEFAULT.availableInTiers] : [];
        } else {
          updated.availableInTiers = value ? [...ALL_TIERS] : [];
        }
      }
      const newState = { ...prev, [key]: updated };

      // Cascade: when websiteAccess is disabled entirely, disable all dependent features
      if (key === 'websiteAccess' && field === 'enabled' && !value) {
        websiteAccessDependentFeatureKeys.forEach(depKey => {
          if (newState[depKey]) {
            newState[depKey] = {
              ...newState[depKey],
              enabled: false,
              availableInTiers: []
            };
          }
        });
      }

      // Cascade: Novo Voice sub-capabilities follow the single voiceAssistant toggle
      if (key === 'voiceAssistant' && field === 'enabled') {
        const parentTiers = value ? [...ALL_TIERS] : [];
        voiceAssistantSubFeatureKeys.forEach(subKey => {
          newState[subKey] = {
            ...(newState[subKey] || { price: 0 }),
            enabled: value,
            availableInTiers: value ? [...parentTiers] : [],
          };
        });
      }

      // Cascade: AI children follow the single aiFeatures toggle
      if (key === 'aiFeatures' && field === 'enabled') {
        const parentTiers = value ? ['professional', 'enterprise'] : [];
        aiFeaturesSubFeatureKeys.forEach(subKey => {
          newState[subKey] = {
            ...(newState[subKey] || { price: 0 }),
            enabled: value,
            availableInTiers: value ? [...parentTiers] : [],
          };
        });
      }

      return newState;
    });
  };

  // Handle global tip management toggle - synced with Tip Management tab
  const handleTipManagementGlobalToggle = (enabled) => {
    setTipManagementGlobalEnabled(enabled);
    
    // When disabling globally, disable all tip management features
    // When enabling globally, enable all tip management features with default tiers
    setFeaturePricing(prev => {
      const updated = { ...prev };
      tipManagementFeatureKeys.forEach(key => {
        if (enabled) {
          // Enable with professional and enterprise tiers by default (not free)
          updated[key] = {
            ...updated[key],
            enabled: true,
            availableInTiers: ['professional', 'enterprise']
          };
        } else {
          // Disable all
          updated[key] = {
            ...updated[key],
            enabled: false,
            availableInTiers: []
          };
        }
      });
      return updated;
    });
  };

  /**
   * Handle tier toggle with cascade logic:
   * - If Free is CHECKED → automatically check Professional and Enterprise
   * - If Enterprise is UNCHECKED → automatically uncheck Professional and Free
   * This ensures tier hierarchy: Free ⊆ Professional ⊆ Enterprise
   */
  const handleTierToggle = (key, tier) => {
    setFeaturePricing(prev => {
      if (TIER_NEUTRAL_FEATURE_KEYS.has(key)) {
        return {
          ...prev,
          [key]: {
            ...prev[key],
            availableInTiers: prev[key]?.enabled ? [...ALL_TIERS] : []
          }
        };
      }

      const currentTiers = prev[key]?.availableInTiers || [];
      const isCurrentlyChecked = currentTiers.includes(tier);
      let newTiers;

      if (tier === 'free') {
        if (!isCurrentlyChecked) {
          // Checking Free → also check Professional and Enterprise
          newTiers = ['free', 'standard', 'professional', 'enterprise'];
        } else {
          // Unchecking Free → just remove Free
          newTiers = currentTiers.filter(t => t !== 'free');
        }
      } else if (tier === 'enterprise') {
        if (isCurrentlyChecked) {
          // Unchecking Enterprise → also uncheck Professional and Free
          newTiers = [];
        } else {
          // Checking Enterprise → just add Enterprise
          newTiers = [...currentTiers.filter(t => t !== 'enterprise'), 'enterprise'];
        }
      } else if (tier === 'professional') {
        if (!isCurrentlyChecked) {
          // Checking Professional → also check Enterprise (if not already)
          newTiers = currentTiers.includes('enterprise')
            ? [...currentTiers, 'professional']
            : [...currentTiers, 'professional', 'enterprise'];
        } else {
          // Unchecking Professional → also uncheck Free (since Free ⊆ Professional)
          newTiers = currentTiers.filter(t => t !== 'professional' && t !== 'free');
        }
      } else {
        // Fallback for unknown tier
        newTiers = isCurrentlyChecked
          ? currentTiers.filter(t => t !== tier)
          : [...currentTiers, tier];
      }

      const updated = { ...prev, [key]: { ...prev[key], availableInTiers: newTiers } };

      // Cascade: when websiteAccess tier is removed, also remove that tier from all dependent features
      if (key === 'websiteAccess') {
        const removedTiers = currentTiers.filter(t => !newTiers.includes(t));
        if (removedTiers.length > 0) {
          websiteAccessDependentFeatureKeys.forEach(depKey => {
            if (updated[depKey]) {
              const depTiers = updated[depKey].availableInTiers || [];
              updated[depKey] = {
                ...updated[depKey],
                availableInTiers: depTiers.filter(t => !removedTiers.includes(t))
              };
            }
          });
        }
      }

      // Cascade: Novo Voice sub-capabilities follow parent tier access
      if (key === 'voiceAssistant') {
        voiceAssistantSubFeatureKeys.forEach(subKey => {
          updated[subKey] = {
            ...(updated[subKey] || { price: 0, enabled: updated.voiceAssistant?.enabled === true }),
            enabled: updated.voiceAssistant?.enabled === true,
            availableInTiers: [...newTiers],
          };
        });
      }

      // Cascade: AI children follow aiFeatures tier access
      if (key === 'aiFeatures') {
        aiFeaturesSubFeatureKeys.forEach(subKey => {
          updated[subKey] = {
            ...(updated[subKey] || { price: 0, enabled: updated.aiFeatures?.enabled === true }),
            enabled: updated.aiFeatures?.enabled === true,
            availableInTiers: [...newTiers],
          };
        });
      }

      return updated;
    });
  };

  // Check if a specific tier has websiteAccess enabled
  const isTierWebsiteAccessEnabled = (tier) => {
    return featurePricing['websiteAccess']?.availableInTiers?.includes(tier) ?? false;
  };

  const handleProductLimitChange = (tier, value) => {
    const maxProducts = value === '' ? null : parseInt(value);
    if (value !== '' && (isNaN(maxProducts) || maxProducts < 0)) return;
    setTierPricing(prev => ({ ...prev, [tier]: { ...prev[tier], features: { ...prev[tier]?.features, maxProducts } } }));
  };

  const handleWebsiteProductLimitChange = (tier, value) => {
    const maxWebsiteProducts = value === '' ? null : parseInt(value);
    if (value !== '' && (isNaN(maxWebsiteProducts) || maxWebsiteProducts < 0)) return;
    setTierPricing(prev => ({ ...prev, [tier]: { ...prev[tier], features: { ...prev[tier]?.features, maxWebsiteProducts } } }));
  };

  const handleCompanyLimitChange = (tier, value) => {
    // Use 1 to effectively disable multi-company for a tier (must allow at least one company for app data integrity).
    const maxCompanies = value === '' ? null : parseInt(value);
    if (value !== '' && (isNaN(maxCompanies) || maxCompanies < 1)) return;
    setTierPricing(prev => ({ ...prev, [tier]: { ...prev[tier], features: { ...prev[tier]?.features, maxCompanies } } }));
  };

  // Handle maxBranches per tier change for multi-location feature
  const handleMaxBranchesChange = (tier, value) => {
    const maxBranches = value === '' ? null : parseInt(value);
    if (value !== '' && (isNaN(maxBranches) || maxBranches < 1)) return;
    setFeaturePricing(prev => ({
      ...prev,
      multiLocationSupport: {
        ...prev.multiLocationSupport,
        maxBranchesPerTier: {
          ...(prev.multiLocationSupport?.maxBranchesPerTier || {}),
          [tier]: maxBranches
        }
      }
    }));
  };

  const handleMultiCompanyGlobalToggle = (enabled) => {
    setFeatureFlags(prev => ({ ...prev, enableMultiCompany: enabled }));
  };

  const handleAIMarketingToggle = (enabled) => {
    setFeatureFlags(prev => ({ ...prev, enableAIMarketing: enabled }));
  };

  const handleNovoMarketToggle = (enabled) => {
    setFeatureFlags(prev => ({ ...prev, enableNovoMarket: enabled }));
  };

  const handleSetupConsultantToggle = (enabled) => {
    setFeatureFlags(prev => ({ ...prev, enableSetupConsultant: enabled }));
  };

  const handleSetupConsultantPilotTenantsChange = (value) => {
    setFeatureFlags(prev => ({
      ...prev,
      setupConsultantPilotTenants: value.split(',').map(v => v.trim()).filter(Boolean)
    }));
  };

  const handleSetupConsultantPilotBusinessTypeToggle = (type, checked) => {
    setFeatureFlags(prev => {
      const current = prev.setupConsultantPilotBusinessTypes || [];
      return {
        ...prev,
        setupConsultantPilotBusinessTypes: checked
          ? [...current.filter(t => t !== type), type]
          : current.filter(t => t !== type)
      };
    });
  };

  const handleSetupConsultantPilotBusinessTypesSelectAll = (checked) => {
    setFeatureFlags(prev => ({
      ...prev,
      setupConsultantPilotBusinessTypes: checked ? ALL_BUSINESS_TYPES.map(bt => bt.value) : []
    }));
  };

  const handleQuickCreateToggle = (enabled) => {
    setFeatureFlags(prev => ({ ...prev, enableQuickCreate: enabled }));
  };

  const handleQuickCreatePilotTenantsChange = (value) => {
    setFeatureFlags(prev => ({
      ...prev,
      quickCreatePilotTenants: value.split(',').map(v => v.trim()).filter(Boolean)
    }));
  };

  const handleQuickCreatePilotBusinessTypeToggle = (type, checked) => {
    setFeatureFlags(prev => {
      const current = prev.quickCreatePilotBusinessTypes || [];
      return {
        ...prev,
        quickCreatePilotBusinessTypes: checked
          ? [...current.filter(t => t !== type), type]
          : current.filter(t => t !== type)
      };
    });
  };

  const handleQuickCreatePilotBusinessTypesSelectAll = (checked) => {
    setFeatureFlags(prev => ({
      ...prev,
      quickCreatePilotBusinessTypes: checked ? ALL_BUSINESS_TYPES.map(bt => bt.value) : []
    }));
  };

  const handleVisualQuickAddToggle = (enabled) => {
    setFeatureFlags(prev => ({ ...prev, enableVisualQuickAdd: enabled }));
  };

  const handleVisualQuickAddPilotTenantsChange = (value) => {
    setFeatureFlags(prev => ({
      ...prev,
      visualQuickAddPilotTenants: value.split(',').map(v => v.trim()).filter(Boolean)
    }));
  };

  const handleVisualQuickAddPilotBusinessTypeToggle = (type, checked) => {
    setFeatureFlags(prev => {
      const current = prev.visualQuickAddPilotBusinessTypes || [];
      return {
        ...prev,
        visualQuickAddPilotBusinessTypes: checked
          ? [...current.filter(t => t !== type), type]
          : current.filter(t => t !== type)
      };
    });
  };

  const handleVisualQuickAddPilotBusinessTypesSelectAll = (checked) => {
    setFeatureFlags(prev => ({
      ...prev,
      visualQuickAddPilotBusinessTypes: checked ? ALL_BUSINESS_TYPES.map(bt => bt.value) : []
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // Include the global tip management flag and tax provider availability in featurePricing
      const hostedPayload = { ...hostedAIPlatform };
      delete hostedPayload.providerApiKeysConfigured;
      delete hostedPayload.providerApiKeysStatus;
      delete hostedPayload.providerApiKeysUnreadable;
      const pk = {};
      if (hostedProviderKeyOpenai.trim()) pk.openai = hostedProviderKeyOpenai.trim();
      if (hostedProviderKeyGemini.trim()) pk.gemini = hostedProviderKeyGemini.trim();
      if (hostedProviderKeyClaude.trim()) pk.claude = hostedProviderKeyClaude.trim();
      if (hostedProviderKeyDeepseek.trim()) pk.deepseek = hostedProviderKeyDeepseek.trim();
      if (hostedProviderKeyKimi.trim()) pk.kimi = hostedProviderKeyKimi.trim();
      if (hostedProviderKeyGlm.trim()) pk.glm = hostedProviderKeyGlm.trim();
      if (Object.keys(pk).length > 0) hostedPayload.providerApiKeys = pk;
      const clr = {};
      if (clearHostedProviderOpenai) clr.openai = true;
      if (clearHostedProviderGemini) clr.gemini = true;
      if (clearHostedProviderClaude) clr.claude = true;
      if (clearHostedProviderDeepseek) clr.deepseek = true;
      if (clearHostedProviderKimi) clr.kimi = true;
      if (clearHostedProviderGlm) clr.glm = true;
      if (Object.keys(clr).length > 0) hostedPayload.clearProviderApiKeys = clr;

      const featurePricingWithGlobal = {
        ...featurePricing,
        hostedAIPlatform: hostedPayload,
        _tipManagementGlobalEnabled: tipManagementGlobalEnabled,
        _taxProviderAvailability: taxProviderAvailability
      };
      
      const response = await fetch('/api/config/feature-pricing', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ featurePricing: featurePricingWithGlobal, tierPricing })
      });
      const data = await response.json();

      if (!data.success) {
        const providers = Array.isArray(data.providers) && data.providers.length > 0
          ? ` Affected provider(s): ${data.providers.join(', ')}.`
          : '';
        setError(`${data.error || 'Failed to save'}${providers}`);
        return;
      }

      if (data.featurePricing?.hostedAIPlatform) {
        setHostedAIPlatform(normalizeHostedAIPlatform(data.featurePricing.hostedAIPlatform));
      }
      setHostedProviderKeyOpenai('');
      setHostedProviderKeyGemini('');
      setHostedProviderKeyClaude('');
      setHostedProviderKeyDeepseek('');
      setHostedProviderKeyKimi('');
      setHostedProviderKeyGlm('');
      setClearHostedProviderOpenai(false);
      setClearHostedProviderGemini(false);
      setClearHostedProviderClaude(false);
      setClearHostedProviderDeepseek(false);
      setClearHostedProviderKimi(false);
      setClearHostedProviderGlm(false);
      setProviderKeyTest({
        openai: { testing: false, result: null },
        gemini: { testing: false, result: null },
        claude: { testing: false, result: null },
        deepseek: { testing: false, result: null },
        kimi: { testing: false, result: null },
        glm: { testing: false, result: null },
      });

      // Save feature flags (global kill-switches)
      const normalizedFeatureFlags = normalizeFeatureFlags(featureFlags);
      const flagsResp = await fetch('/api/config/feature-flags', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ featureFlags: normalizedFeatureFlags })
      });
      const flagsData = await flagsResp.json();
      if (!flagsData.success) {
        setError(flagsData.error || 'Failed to save feature flags');
        return;
      }

      setFeatureFlags(normalizedFeatureFlags);

      setError(null);
      setSuccess('Configuration saved! Version: ' + data.version);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ textAlign: 'center', p: 4 }}><CircularProgress /></Box>;

  const providerKeyActionRequired = ['openai', 'gemini', 'claude', 'deepseek', 'kimi', 'glm'].filter(
    (provider) => hostedAIPlatform.providerApiKeysUnreadable?.[provider]
  );
  const providerKeyActionRequiredLabels = providerKeyActionRequired
    .map((provider) => {
      if (provider === 'openai') return 'OpenAI';
      if (provider === 'gemini') return 'Gemini';
      if (provider === 'claude') return 'Claude';
      if (provider === 'deepseek') return 'DeepSeek';
      if (provider === 'kimi') return 'Kimi';
      return 'GLM';
    })
    .join(', ');
  const providerKeyChip = (provider, label) => {
    if (hostedAIPlatform.providerApiKeysUnreadable?.[provider]) {
      return { label: `${label}: re-save key`, color: 'warning' };
    }
    if (hostedAIPlatform.providerApiKeysConfigured?.[provider]) {
      return { label: `${label}: saved`, color: 'success' };
    }
    return { label: `${label}: not set`, color: 'default' };
  };
  const openaiKeyChip = providerKeyChip('openai', 'OpenAI');
  const geminiKeyChip = providerKeyChip('gemini', 'Gemini');
  const claudeKeyChip = providerKeyChip('claude', 'Claude');
  const deepseekKeyChip = providerKeyChip('deepseek', 'DeepSeek');
  const kimiKeyChip = providerKeyChip('kimi', 'Kimi');
  const glmKeyChip = providerKeyChip('glm', 'GLM');

  return (
    <Box sx={{ pb: error || success ? '140px' : '88px' }}>
      <Alert severity="warning" sx={{ mb: 3 }}>
        <strong>⚠️ MASTER CONTROL</strong> - This actually enables/disables features in the iOS app.
        Changes take effect within 1 hour (cache refresh).
      </Alert>

      <Accordion
        defaultExpanded
        sx={{ mb: 1, border: '1px solid', borderColor: 'warning.light', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h6">Novo Voice Debug Only</Typography>
            <Chip
              label={featurePricing.voiceSurfacesDebugOnly?.enabled !== false ? 'DEBUG ONLY' : 'DEBUG AND RELEASE'}
              size="small"
              color={featurePricing.voiceSurfacesDebugOnly?.enabled !== false ? 'warning' : 'success'}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            On: Novo Voice and account voice setup show only in debug builds, even when Novo Voice is enabled below.
            Off: they show in debug and release builds when their own switches are on.
            Save this page after changing it.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={featurePricing.voiceSurfacesDebugOnly?.enabled !== false}
                onChange={(e) => handleFeatureToggle('voiceSurfacesDebugOnly', 'enabled', e.target.checked)}
                color="warning"
              />
            }
            label={
              featurePricing.voiceSurfacesDebugOnly?.enabled !== false
                ? 'ON — hidden in release builds'
                : 'OFF — visible in debug and release builds'
            }
            labelPlacement="start"
          />
        </AccordionDetails>
      </Accordion>

      {/* Novo-hosted AI — controlled accordion, collapsed until expanded */}
      <Accordion
        expanded={hostedAiSectionExpanded}
        onChange={(_, isExpanded) => setHostedAiSectionExpanded(isExpanded)}
        id="bi-hosted-ai-settings"
        elevation={0}
        sx={{
          mb: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="bi-hosted-ai-panel-content"
          id="bi-hosted-ai-panel-header"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%', pr: 1 }}>
            <SmartToyIcon color="primary" fontSize="medium" />
            <Typography variant="subtitle1" component="span" sx={{ fontWeight: 600 }}>
              Hosted AI (Novo-provided)
            </Typography>
            <Chip
              size="small"
              label={hostedAIPlatform?.enabled === false ? 'OFF' : 'ON'}
              color={hostedAIPlatform?.enabled === false ? 'default' : 'success'}
            />
            {hostedAIPlatform?.enabled !== false && hostedAIPlatform?.setupConsultantHostedGrantEnabled !== false && (
              <Chip size="small" color="primary" variant="outlined" label="Interview" />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails id="bi-hosted-ai-panel-content">
          <Stack spacing={3}>
            <Alert severity="info" sx={{ py: 1.5 }}>
              <Typography variant="body2" component="div">
                This accordion controls <strong>Novo-provided hosted AI only</strong> (server-side models and credits).
                Turning this master switch off does <strong>not</strong> disable a customer&apos;s own OpenAI/Gemini/Claude/DeepSeek/Kimi/GLM keys.
                Use the separate <strong>Customer AI (Bring Your Own Key)</strong> accordion below to hide AI API Keys
                and AI Write capsules in the iOS app.
                <strong> Interview</strong> lets the Setup Consultant use Novo-hosted AI during onboarding.
                It does not show the interview by itself — that is the <strong>Setup Consultant</strong> kill switch
                further down this page. It also does not unlock the hosted catalogue after signup.
              </Typography>
            </Alert>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Master control
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={hostedAIPlatform.enabled !== false}
                    onChange={(e) =>
                      setHostedAIPlatform((p) => ({ ...p, enabled: e.target.checked }))
                    }
                  />
                }
                label="Novo-hosted AI enabled (does not affect customer API keys)"
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Available to
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Select which subscription tiers may use the Novo-hosted pool, plus Interview for first-run setup.
              </Typography>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hostedAIPlatform.setupConsultantHostedGrantEnabled !== false}
                      disabled={hostedAIPlatform.enabled === false}
                      onChange={(e) =>
                        setHostedAIPlatform((p) => ({
                          ...p,
                          setupConsultantHostedGrantEnabled: e.target.checked,
                        }))
                      }
                      size="small"
                    />
                  }
                  label={
                    <Tooltip title="Setup Consultant during onboarding and account creation. Does not unlock hosted AI after signup.">
                      <Chip
                        label="Interview"
                        size="small"
                        color="primary"
                        variant={hostedAIPlatform.setupConsultantHostedGrantEnabled !== false ? 'filled' : 'outlined'}
                      />
                    </Tooltip>
                  }
                />
                {['free', 'standard', 'professional', 'enterprise'].map((tier) => {
                  const isTierChecked = hostedAIPlatform.tiers?.[tier]?.hostedAIEnabled === true;
                  return (
                    <FormControlLabel
                      key={`hai-available-${tier}`}
                      control={
                        <Checkbox
                          checked={isTierChecked}
                          disabled={hostedAIPlatform.enabled === false}
                          onChange={(e) =>
                            setHostedAIPlatform((p) => ({
                              ...p,
                              tiers: {
                                ...defaultHostedAIPlatform().tiers,
                                ...p.tiers,
                                [tier]: {
                                  ...defaultHostedAIPlatform().tiers[tier],
                                  ...p.tiers?.[tier],
                                  hostedAIEnabled: e.target.checked,
                                },
                              },
                            }))
                          }
                          size="small"
                        />
                      }
                      label={
                        <Chip
                          label={tier.charAt(0).toUpperCase() + tier.slice(1)}
                          size="small"
                          variant={isTierChecked ? 'filled' : 'outlined'}
                        />
                      }
                    />
                  );
                })}
              </FormGroup>
            </Box>

            <Paper
              id="bi-hosted-ai-api-keys"
              variant="outlined"
              sx={{
                p: 2.5,
                borderColor: 'primary.main',
                borderWidth: 2,
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.08)' : 'rgba(25, 118, 210, 0.06)',
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }} justifyContent="space-between" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Provider &amp; API keys
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Keys are encrypted at rest. Set <code>BI_HOSTED_AI_KEYS_SECRET</code> (32+ characters) on this BI server before
                    saving. The website retrieves keys using the same internal secret as hosted-config cache invalidation.
                    After you paste a key, tap Refresh models to list every chat model that key can access, then check the ones
                    Novo hosted AI may offer.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => setHostedCatalogRefreshToken((token) => token + 1)}
                  sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  Refresh models
                </Button>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Default hosted model / route"
                    value={hostedAIPlatform.hostedProvider || 'gemini_flash'}
                    onChange={(e) =>
                      setHostedAIPlatform((p) => ({ ...p, hostedProvider: e.target.value }))
                    }
                    SelectProps={{ native: true }}
                    helperText="Primary family when a customer has not picked a specific hosted model. GPT-5 is available only if you allowlist it after Refresh models and the OpenAI org is verified."
                  >
                    <option value="gemini_flash">Gemini 2.5 Flash</option>
                    <option value="gemini_pro">Gemini 2.5 Pro</option>
                    <option value="openai_gpt4o_mini">OpenAI GPT-4o mini</option>
                    <option value="openai_gpt5">OpenAI GPT-4o (flagship)</option>
                    <option value="claude_sonnet">Claude Sonnet</option>
                    <option value="claude_opus">Claude Opus</option>
                    <option value="deepseek_flash">DeepSeek V4 Flash</option>
                    <option value="deepseek_pro">DeepSeek V4 Pro</option>
                    <option value="kimi_k2">Kimi K2.6</option>
                    <option value="kimi_k3">Kimi K3</option>
                    <option value="glm_flash">GLM 4.7 Flash</option>
                    <option value="glm_pro">GLM 5.2</option>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={hostedAIPlatform.hostedProviderFailoverEnabled !== false}
                        onChange={(e) =>
                          setHostedAIPlatform((p) => ({
                            ...p,
                            hostedProviderFailoverEnabled: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Fail over on HTTP 429 / 5xx (requires another API key)"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Failover stays within the resolved family first (for example GPT-4o → mini, GLM Pro → Flash), then the
                    existing cross-family 429/5xx secondary if that is still enabled.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Credential status
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip
                      size="small"
                      label={openaiKeyChip.label}
                      color={openaiKeyChip.color}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={geminiKeyChip.label}
                      color={geminiKeyChip.color}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={claudeKeyChip.label}
                      color={claudeKeyChip.color}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={deepseekKeyChip.label}
                      color={deepseekKeyChip.color}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={kimiKeyChip.label}
                      color={kimiKeyChip.color}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={glmKeyChip.label}
                      color={glmKeyChip.color}
                      variant="outlined"
                    />
                  </Box>
                  {providerKeyActionRequired.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 1, py: 0.75 }}>
                      Action Required: Re-save Key for {providerKeyActionRequiredLabels}. Saved-key metadata exists,
                      but the encrypted key cannot be read.
                      This can happen after a BI_HOSTED_AI_KEYS_SECRET change. Paste the affected key again, save,
                      then test it.
                    </Alert>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      fullWidth
                      type="password"
                      autoComplete="new-password"
                      label="OpenAI API key"
                      placeholder={
                        hostedAIPlatform.providerApiKeysUnreadable?.openai
                          ? 'Re-enter key to repair stored credential'
                          : hostedAIPlatform.providerApiKeysConfigured?.openai ? 'Leave blank to keep existing' : 'sk-…'
                      }
                      value={hostedProviderKeyOpenai}
                      onChange={(e) => {
                        setHostedProviderKeyOpenai(e.target.value);
                        setProviderKeyTest((prev) => ({ ...prev, openai: { testing: false, result: null } }));
                      }}
                      helperText={
                        hostedAIPlatform.providerApiKeysUnreadable?.openai
                          ? 'Action required: paste the key and save to replace the unreadable stored credential.'
                          : 'Stored encrypted. Empty = leave unchanged.'
                      }
                    />
                    <Button
                      variant="outlined"
                      size="medium"
                      onClick={() => handleTestProviderKey('openai')}
                      disabled={
                        providerKeyTest.openai.testing ||
                        (!hostedProviderKeyOpenai.trim() && !hostedAIPlatform.providerApiKeysConfigured?.openai)
                      }
                      sx={{ mt: 1, whiteSpace: 'nowrap', minWidth: 88 }}
                    >
                      {providerKeyTest.openai.testing ? <CircularProgress size={18} /> : 'Test'}
                    </Button>
                  </Stack>
                  {providerKeyTest.openai.result && (
                    <Alert
                      severity={providerKeyTest.openai.result.valid ? 'success' : 'error'}
                      sx={{ mt: 1, py: 0.5 }}
                      onClose={() =>
                        setProviderKeyTest((prev) => ({ ...prev, openai: { testing: false, result: null } }))
                      }
                    >
                      {providerKeyTest.openai.result.message}
                      {providerKeyTest.openai.result.model ? ` (sample model: ${providerKeyTest.openai.result.model})` : ''}
                    </Alert>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      fullWidth
                      type="password"
                      autoComplete="new-password"
                      label="Gemini API key"
                      placeholder={
                        hostedAIPlatform.providerApiKeysUnreadable?.gemini
                          ? 'Re-enter key to repair stored credential'
                          : hostedAIPlatform.providerApiKeysConfigured?.gemini ? 'Leave blank to keep existing' : 'Paste key…'
                      }
                      value={hostedProviderKeyGemini}
                      onChange={(e) => {
                        setHostedProviderKeyGemini(e.target.value);
                        setProviderKeyTest((prev) => ({ ...prev, gemini: { testing: false, result: null } }));
                      }}
                      helperText={
                        hostedAIPlatform.providerApiKeysUnreadable?.gemini
                          ? 'Action required: paste the key and save to replace the unreadable stored credential.'
                          : 'Stored encrypted. Empty = leave unchanged.'
                      }
                    />
                    <Button
                      variant="outlined"
                      size="medium"
                      onClick={() => handleTestProviderKey('gemini')}
                      disabled={
                        providerKeyTest.gemini.testing ||
                        (!hostedProviderKeyGemini.trim() && !hostedAIPlatform.providerApiKeysConfigured?.gemini)
                      }
                      sx={{ mt: 1, whiteSpace: 'nowrap', minWidth: 88 }}
                    >
                      {providerKeyTest.gemini.testing ? <CircularProgress size={18} /> : 'Test'}
                    </Button>
                  </Stack>
                  {providerKeyTest.gemini.result && (
                    <Alert
                      severity={providerKeyTest.gemini.result.valid ? 'success' : 'error'}
                      sx={{ mt: 1, py: 0.5 }}
                      onClose={() =>
                        setProviderKeyTest((prev) => ({ ...prev, gemini: { testing: false, result: null } }))
                      }
                    >
                      {providerKeyTest.gemini.result.message}
                      {providerKeyTest.gemini.result.model ? ` (sample model: ${providerKeyTest.gemini.result.model})` : ''}
                    </Alert>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      fullWidth
                      type="password"
                      autoComplete="new-password"
                      label="Claude API key"
                      placeholder={
                        hostedAIPlatform.providerApiKeysUnreadable?.claude
                          ? 'Re-enter key to repair stored credential'
                          : hostedAIPlatform.providerApiKeysConfigured?.claude ? 'Leave blank to keep existing' : 'sk-ant-…'
                      }
                      value={hostedProviderKeyClaude}
                      onChange={(e) => {
                        setHostedProviderKeyClaude(e.target.value);
                        setProviderKeyTest((prev) => ({ ...prev, claude: { testing: false, result: null } }));
                      }}
                      helperText={
                        hostedAIPlatform.providerApiKeysUnreadable?.claude
                          ? 'Action required: paste the key and save to replace the unreadable stored credential.'
                          : 'Stored encrypted. Empty = leave unchanged. Get a key at console.anthropic.com.'
                      }
                    />
                    <Button
                      variant="outlined"
                      size="medium"
                      onClick={() => handleTestProviderKey('claude')}
                      disabled={
                        providerKeyTest.claude.testing ||
                        (!hostedProviderKeyClaude.trim() && !hostedAIPlatform.providerApiKeysConfigured?.claude)
                      }
                      sx={{ mt: 1, whiteSpace: 'nowrap', minWidth: 88 }}
                    >
                      {providerKeyTest.claude.testing ? <CircularProgress size={18} /> : 'Test'}
                    </Button>
                  </Stack>
                  {providerKeyTest.claude.result && (
                    <Alert
                      severity={providerKeyTest.claude.result.valid ? 'success' : 'error'}
                      sx={{ mt: 1, py: 0.5 }}
                      onClose={() =>
                        setProviderKeyTest((prev) => ({ ...prev, claude: { testing: false, result: null } }))
                      }
                    >
                      {providerKeyTest.claude.result.message}
                      {providerKeyTest.claude.result.model ? ` (sample model: ${providerKeyTest.claude.result.model})` : ''}
                    </Alert>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      fullWidth
                      type="password"
                      autoComplete="new-password"
                      label="DeepSeek API key"
                      placeholder={
                        hostedAIPlatform.providerApiKeysUnreadable?.deepseek
                          ? 'Re-enter key to repair stored credential'
                          : hostedAIPlatform.providerApiKeysConfigured?.deepseek ? 'Leave blank to keep existing' : 'sk-…'
                      }
                      value={hostedProviderKeyDeepseek}
                      onChange={(e) => {
                        setHostedProviderKeyDeepseek(e.target.value);
                        setProviderKeyTest((prev) => ({ ...prev, deepseek: { testing: false, result: null } }));
                      }}
                      helperText={
                        hostedAIPlatform.providerApiKeysUnreadable?.deepseek
                          ? 'Action required: paste the key and save to replace the unreadable stored credential.'
                          : 'Stored encrypted. Empty = leave unchanged. Get a key at platform.deepseek.com.'
                      }
                    />
                    <Button
                      variant="outlined"
                      size="medium"
                      onClick={() => handleTestProviderKey('deepseek')}
                      disabled={
                        providerKeyTest.deepseek.testing ||
                        (!hostedProviderKeyDeepseek.trim() && !hostedAIPlatform.providerApiKeysConfigured?.deepseek)
                      }
                      sx={{ mt: 1, whiteSpace: 'nowrap', minWidth: 88 }}
                    >
                      {providerKeyTest.deepseek.testing ? <CircularProgress size={18} /> : 'Test'}
                    </Button>
                  </Stack>
                  {providerKeyTest.deepseek.result && (
                    <Alert
                      severity={providerKeyTest.deepseek.result.valid ? 'success' : 'error'}
                      sx={{ mt: 1, py: 0.5 }}
                      onClose={() =>
                        setProviderKeyTest((prev) => ({ ...prev, deepseek: { testing: false, result: null } }))
                      }
                    >
                      {providerKeyTest.deepseek.result.message}
                      {providerKeyTest.deepseek.result.model ? ` (sample model: ${providerKeyTest.deepseek.result.model})` : ''}
                    </Alert>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      fullWidth
                      type="password"
                      autoComplete="new-password"
                      label="Kimi API key"
                      placeholder={
                        hostedAIPlatform.providerApiKeysUnreadable?.kimi
                          ? 'Re-enter key to repair stored credential'
                          : hostedAIPlatform.providerApiKeysConfigured?.kimi ? 'Leave blank to keep existing' : 'Paste key…'
                      }
                      value={hostedProviderKeyKimi}
                      onChange={(e) => {
                        setHostedProviderKeyKimi(e.target.value);
                        setProviderKeyTest((prev) => ({ ...prev, kimi: { testing: false, result: null } }));
                      }}
                      helperText={
                        hostedAIPlatform.providerApiKeysUnreadable?.kimi
                          ? 'Action required: paste the key and save to replace the unreadable stored credential.'
                          : 'Stored encrypted. Empty = leave unchanged. Get a key at platform.moonshot.ai.'
                      }
                    />
                    <Button
                      variant="outlined"
                      size="medium"
                      onClick={() => handleTestProviderKey('kimi')}
                      disabled={
                        providerKeyTest.kimi.testing ||
                        (!hostedProviderKeyKimi.trim() && !hostedAIPlatform.providerApiKeysConfigured?.kimi)
                      }
                      sx={{ mt: 1, whiteSpace: 'nowrap', minWidth: 88 }}
                    >
                      {providerKeyTest.kimi.testing ? <CircularProgress size={18} /> : 'Test'}
                    </Button>
                  </Stack>
                  {providerKeyTest.kimi.result && (
                    <Alert
                      severity={providerKeyTest.kimi.result.valid ? 'success' : 'error'}
                      sx={{ mt: 1, py: 0.5 }}
                      onClose={() =>
                        setProviderKeyTest((prev) => ({ ...prev, kimi: { testing: false, result: null } }))
                      }
                    >
                      {providerKeyTest.kimi.result.message}
                      {providerKeyTest.kimi.result.model ? ` (sample model: ${providerKeyTest.kimi.result.model})` : ''}
                    </Alert>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      fullWidth
                      type="password"
                      autoComplete="new-password"
                      label="GLM (Z.AI) API key"
                      placeholder={
                        hostedAIPlatform.providerApiKeysUnreadable?.glm
                          ? 'Re-enter key to repair stored credential'
                          : hostedAIPlatform.providerApiKeysConfigured?.glm ? 'Leave blank to keep existing' : 'Paste key…'
                      }
                      value={hostedProviderKeyGlm}
                      onChange={(e) => {
                        setHostedProviderKeyGlm(e.target.value);
                        setProviderKeyTest((prev) => ({ ...prev, glm: { testing: false, result: null } }));
                      }}
                      helperText={
                        hostedAIPlatform.providerApiKeysUnreadable?.glm
                          ? 'Action required: paste the key and save to replace the unreadable stored credential.'
                          : 'Stored encrypted. Empty = leave unchanged. Get a key at z.ai.'
                      }
                    />
                    <Button
                      variant="outlined"
                      size="medium"
                      onClick={() => handleTestProviderKey('glm')}
                      disabled={
                        providerKeyTest.glm.testing ||
                        (!hostedProviderKeyGlm.trim() && !hostedAIPlatform.providerApiKeysConfigured?.glm)
                      }
                      sx={{ mt: 1, whiteSpace: 'nowrap', minWidth: 88 }}
                    >
                      {providerKeyTest.glm.testing ? <CircularProgress size={18} /> : 'Test'}
                    </Button>
                  </Stack>
                  {providerKeyTest.glm.result && (
                    <Alert
                      severity={providerKeyTest.glm.result.valid ? 'success' : 'error'}
                      sx={{ mt: 1, py: 0.5 }}
                      onClose={() =>
                        setProviderKeyTest((prev) => ({ ...prev, glm: { testing: false, result: null } }))
                      }
                    >
                      {providerKeyTest.glm.result.message}
                      {providerKeyTest.glm.result.model ? ` (sample model: ${providerKeyTest.glm.result.model})` : ''}
                    </Alert>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <FormGroup row>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={clearHostedProviderOpenai}
                          onChange={(e) => setClearHostedProviderOpenai(e.target.checked)}
                          disabled={
                            !hostedAIPlatform.providerApiKeysConfigured?.openai &&
                            !hostedAIPlatform.providerApiKeysUnreadable?.openai
                          }
                        />
                      }
                      label="Remove stored OpenAI key"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={clearHostedProviderGemini}
                          onChange={(e) => setClearHostedProviderGemini(e.target.checked)}
                          disabled={
                            !hostedAIPlatform.providerApiKeysConfigured?.gemini &&
                            !hostedAIPlatform.providerApiKeysUnreadable?.gemini
                          }
                        />
                      }
                      label="Remove stored Gemini key"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={clearHostedProviderClaude}
                          onChange={(e) => setClearHostedProviderClaude(e.target.checked)}
                          disabled={
                            !hostedAIPlatform.providerApiKeysConfigured?.claude &&
                            !hostedAIPlatform.providerApiKeysUnreadable?.claude
                          }
                        />
                      }
                      label="Remove stored Claude key"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={clearHostedProviderDeepseek}
                          onChange={(e) => setClearHostedProviderDeepseek(e.target.checked)}
                          disabled={
                            !hostedAIPlatform.providerApiKeysConfigured?.deepseek &&
                            !hostedAIPlatform.providerApiKeysUnreadable?.deepseek
                          }
                        />
                      }
                      label="Remove stored DeepSeek key"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={clearHostedProviderKimi}
                          onChange={(e) => setClearHostedProviderKimi(e.target.checked)}
                          disabled={
                            !hostedAIPlatform.providerApiKeysConfigured?.kimi &&
                            !hostedAIPlatform.providerApiKeysUnreadable?.kimi
                          }
                        />
                      }
                      label="Remove stored Kimi key"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={clearHostedProviderGlm}
                          onChange={(e) => setClearHostedProviderGlm(e.target.checked)}
                          disabled={
                            !hostedAIPlatform.providerApiKeysConfigured?.glm &&
                            !hostedAIPlatform.providerApiKeysUnreadable?.glm
                          }
                        />
                      }
                      label="Remove stored GLM key"
                    />
                  </FormGroup>
                </Grid>
              </Grid>
            </Paper>

            <HostedAIAvailableModels
              enabledModels={hostedAIPlatform.enabledModels}
              onChange={(enabledModels) =>
                setHostedAIPlatform((p) => ({ ...p, enabledModels }))
              }
              providerApiKeysConfigured={hostedAIPlatform.providerApiKeysConfigured}
              typedKeys={{
                openai: hostedProviderKeyOpenai,
                gemini: hostedProviderKeyGemini,
                claude: hostedProviderKeyClaude,
                deepseek: hostedProviderKeyDeepseek,
                kimi: hostedProviderKeyKimi,
                glm: hostedProviderKeyGlm,
              }}
              expanded={hostedAiSectionExpanded}
              refreshToken={hostedCatalogRefreshToken}
            />

            <HostedAISectionRouting
              sectionAssignments={hostedAIPlatform.sectionAssignments}
              languageAssignments={hostedAIPlatform.languageAssignments}
              enabledModels={hostedAIPlatform.enabledModels}
              providerApiKeysConfigured={hostedAIPlatform.providerApiKeysConfigured}
              typedKeys={{
                openai: hostedProviderKeyOpenai,
                gemini: hostedProviderKeyGemini,
                claude: hostedProviderKeyClaude,
                deepseek: hostedProviderKeyDeepseek,
                kimi: hostedProviderKeyKimi,
                glm: hostedProviderKeyGlm,
              }}
              onChange={({ sectionAssignments, languageAssignments }) =>
                setHostedAIPlatform((p) => ({
                  ...p,
                  sectionAssignments,
                  languageAssignments,
                }))
              }
            />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Rate limits &amp; spend
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Per-company requests / minute"
                    value={hostedAIPlatform.abuseControls?.perCompanyRequestsPerMinute ?? 20}
                    onChange={(e) =>
                      setHostedAIPlatform((p) => ({
                        ...p,
                        abuseControls: {
                          ...defaultHostedAIPlatform().abuseControls,
                          ...p.abuseControls,
                          perCompanyRequestsPerMinute: parseInt(e.target.value, 10) || 0,
                        },
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Monthly spend cap (cents, empty = none)"
                    value={hostedAIPlatform.abuseControls?.monthlyProviderSpendCapCents ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setHostedAIPlatform((p) => ({
                        ...p,
                        abuseControls: {
                          ...defaultHostedAIPlatform().abuseControls,
                          ...p.abuseControls,
                          monthlyProviderSpendCapCents: v === '' ? null : parseInt(v, 10),
                        },
                      }));
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                Per-tier credits &amp; bulk
              </Typography>
              <Grid container spacing={2}>
            {['free', 'standard', 'professional', 'enterprise'].map((tier) => (
              <Grid item xs={12} sm={6} md={3} key={`hai-${tier}`}>
                <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, textTransform: 'capitalize', fontWeight: 600 }}>
                  {tier} tier
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      label="Monthly credits"
                      value={hostedAIPlatform.tiers?.[tier]?.monthlyPromptLimit ?? 0}
                      onChange={(e) =>
                        setHostedAIPlatform((p) => ({
                          ...p,
                          tiers: {
                            ...defaultHostedAIPlatform().tiers,
                            ...p.tiers,
                            [tier]: {
                              ...defaultHostedAIPlatform().tiers[tier],
                              ...p.tiers?.[tier],
                              monthlyPromptLimit: parseInt(e.target.value, 10) || 0,
                            },
                          },
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      label="Daily credits"
                      value={hostedAIPlatform.tiers?.[tier]?.dailyPromptLimit ?? 0}
                      onChange={(e) =>
                        setHostedAIPlatform((p) => ({
                          ...p,
                          tiers: {
                            ...defaultHostedAIPlatform().tiers,
                            ...p.tiers,
                            [tier]: {
                              ...defaultHostedAIPlatform().tiers[tier],
                              ...p.tiers?.[tier],
                              dailyPromptLimit: parseInt(e.target.value, 10) || 0,
                            },
                          },
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={hostedAIPlatform.tiers?.[tier]?.hostedAIEnabled === true}
                          onChange={(e) =>
                            setHostedAIPlatform((p) => ({
                              ...p,
                              tiers: {
                                ...defaultHostedAIPlatform().tiers,
                                ...p.tiers,
                                [tier]: {
                                  ...defaultHostedAIPlatform().tiers[tier],
                                  ...p.tiers?.[tier],
                                  hostedAIEnabled: e.target.checked,
                                },
                              },
                            }))
                          }
                        />
                      }
                      label="Hosted AI enabled"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={hostedAIPlatform.tiers?.[tier]?.bulkOperationsEnabled === true}
                          onChange={(e) =>
                            setHostedAIPlatform((p) => ({
                              ...p,
                              tiers: {
                                ...defaultHostedAIPlatform().tiers,
                                ...p.tiers,
                                [tier]: {
                                  ...defaultHostedAIPlatform().tiers[tier],
                                  ...p.tiers?.[tier],
                                  bulkOperationsEnabled: e.target.checked,
                                },
                              },
                            }))
                          }
                        />
                      }
                      label="Bulk operations"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      label="Max items per bulk job"
                      value={hostedAIPlatform.tiers?.[tier]?.maxItemsPerBulkJob ?? 0}
                      onChange={(e) =>
                        setHostedAIPlatform((p) => ({
                          ...p,
                          tiers: {
                            ...defaultHostedAIPlatform().tiers,
                            ...p.tiers,
                            [tier]: {
                              ...defaultHostedAIPlatform().tiers[tier],
                              ...p.tiers?.[tier],
                              maxItemsPerBulkJob: parseInt(e.target.value, 10) || 0,
                            },
                          },
                        }))
                      }
                    />
                  </Grid>
                </Grid>
                </Paper>
              </Grid>
            ))}
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Platform bulk &amp; deduplication
              </Typography>
              <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Bulk: max items / job"
                value={hostedAIPlatform.bulkLimits?.maxItemsPerJob ?? 500}
                onChange={(e) =>
                  setHostedAIPlatform((p) => ({
                    ...p,
                    bulkLimits: {
                      ...defaultHostedAIPlatform().bulkLimits,
                      ...p.bulkLimits,
                      maxItemsPerJob: parseInt(e.target.value, 10) || 0,
                    },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Bulk: max jobs / day"
                value={hostedAIPlatform.bulkLimits?.maxBulkJobsPerDay ?? 10}
                onChange={(e) =>
                  setHostedAIPlatform((p) => ({
                    ...p,
                    bulkLimits: {
                      ...defaultHostedAIPlatform().bulkLimits,
                      ...p.bulkLimits,
                      maxBulkJobsPerDay: parseInt(e.target.value, 10) || 0,
                    },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Dedup TTL (seconds)"
                value={hostedAIPlatform.deduplication?.ttlSeconds ?? 604800}
                onChange={(e) =>
                  setHostedAIPlatform((p) => ({
                    ...p,
                    deduplication: {
                      ...defaultHostedAIPlatform().deduplication,
                      ...p.deduplication,
                      ttlSeconds: parseInt(e.target.value, 10) || 60,
                    },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={hostedAIPlatform.deduplication?.enabled !== false}
                    onChange={(e) =>
                      setHostedAIPlatform((p) => ({
                        ...p,
                        deduplication: {
                          ...defaultHostedAIPlatform().deduplication,
                          ...p.deduplication,
                          enabled: e.target.checked,
                        },
                      }))
                    }
                  />
                }
                label="Dedup enabled"
              />
            </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                BYOK fallback
              </Typography>
              <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={hostedAIPlatform.byokFallback?.preferBYOKWhenConfigured !== false}
                    onChange={(e) =>
                      setHostedAIPlatform((p) => ({
                        ...p,
                        byokFallback: {
                          ...defaultHostedAIPlatform().byokFallback,
                          ...p.byokFallback,
                          preferBYOKWhenConfigured: e.target.checked,
                        },
                      }))
                    }
                  />
                }
                label="Prefer BYOK when configured"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={hostedAIPlatform.byokFallback?.autoFallbackOnHostedExhaustion !== false}
                    onChange={(e) =>
                      setHostedAIPlatform((p) => ({
                        ...p,
                        byokFallback: {
                          ...defaultHostedAIPlatform().byokFallback,
                          ...p.byokFallback,
                          autoFallbackOnHostedExhaustion: e.target.checked,
                        },
                      }))
                    }
                  />
                }
                label="Auto-fallback to BYOK when hosted blocks"
              />
            </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Quotas, caps &amp; blocking
              </Typography>
              <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Monthly quota reset"
                value={hostedAIPlatform.monthlyReset || 'UTC_CALENDAR_MONTH'}
                onChange={(e) =>
                  setHostedAIPlatform((p) => ({ ...p, monthlyReset: e.target.value }))
                }
                SelectProps={{ native: true }}
              >
                <option value="UTC_CALENDAR_MONTH">UTC calendar month</option>
                <option value="SUBSCRIPTION_RENEWAL">Subscription renewal (if configured)</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Weekly cap reset"
                value={hostedAIPlatform.weeklyReset || 'UTC_ISO_WEEK_MONDAY'}
                onChange={(e) =>
                  setHostedAIPlatform((p) => ({ ...p, weeklyReset: e.target.value }))
                }
                SelectProps={{ native: true }}
              >
                <option value="UTC_ISO_WEEK_MONDAY">ISO week (Monday UTC)</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Block operations from hosted (comma-separated ids)"
                placeholder="image_generation, video_generation"
                value={(Array.isArray(hostedAIPlatform.operationsBlockedFromHosted)
                  ? hostedAIPlatform.operationsBlockedFromHosted
                  : []
                ).join(', ')}
                onChange={(e) => {
                  const parts = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
                  setHostedAIPlatform((p) => ({ ...p, operationsBlockedFromHosted: parts }));
                }}
                helperText="Server rejects these operation types on the hosted pool."
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Max input tokens (global cap)"
                value={hostedAIPlatform.abuseControls?.maxInputTokens ?? 64000}
                onChange={(e) =>
                  setHostedAIPlatform((p) => ({
                    ...p,
                    abuseControls: {
                      ...defaultHostedAIPlatform().abuseControls,
                      ...p.abuseControls,
                      maxInputTokens: parseInt(e.target.value, 10) || 0,
                    },
                  }))
                }
                helperText="Applies to small high-volume ops (alt text, SEO, blog, voice…). Advanced Editor (page_editor_*) ops use their own per-operation limits and are not clamped by this."
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Max output tokens (global cap)"
                value={hostedAIPlatform.abuseControls?.maxOutputTokens ?? 8000}
                onChange={(e) =>
                  setHostedAIPlatform((p) => ({
                    ...p,
                    abuseControls: {
                      ...defaultHostedAIPlatform().abuseControls,
                      ...p.abuseControls,
                      maxOutputTokens: parseInt(e.target.value, 10) || 0,
                    },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Max concurrent requests / company"
                value={hostedAIPlatform.abuseControls?.maxConcurrentRequestsPerCompany ?? 3}
                onChange={(e) =>
                  setHostedAIPlatform((p) => ({
                    ...p,
                    abuseControls: {
                      ...defaultHostedAIPlatform().abuseControls,
                      ...p.abuseControls,
                      maxConcurrentRequestsPerCompany: parseInt(e.target.value, 10) || 0,
                    },
                  }))
                }
              />
            </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Usage notifications
              </Typography>
              <TextField
                fullWidth
                size="small"
                label="Warn thresholds (%), comma-separated"
                value={(Array.isArray(hostedAIPlatform.usageNotifications?.warnAtPercent)
                  ? hostedAIPlatform.usageNotifications.warnAtPercent
                  : []
                ).join(', ')}
                onChange={(e) => {
                  const nums = e.target.value.split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !Number.isNaN(n));
                  setHostedAIPlatform((p) => ({
                    ...p,
                    usageNotifications: {
                      ...defaultHostedAIPlatform().usageNotifications,
                      ...p.usageNotifications,
                      warnAtPercent: nums.length ? nums : [50, 75, 90, 100],
                    },
                  }));
                }}
                sx={{ mb: 1 }}
              />
              <FormGroup row>
                {['app_banner', 'email_admin'].map((ch) => (
                  <FormControlLabel
                    key={ch}
                    control={
                      <Checkbox
                        size="small"
                        checked={(Array.isArray(hostedAIPlatform.usageNotifications?.notifyChannels)
                          ? hostedAIPlatform.usageNotifications.notifyChannels
                          : ['app_banner']
                        ).includes(ch)}
                        onChange={(e) => {
                          const cur = new Set(
                            Array.isArray(hostedAIPlatform.usageNotifications?.notifyChannels)
                              ? hostedAIPlatform.usageNotifications.notifyChannels
                              : ['app_banner']
                          );
                          if (e.target.checked) cur.add(ch);
                          else cur.delete(ch);
                          setHostedAIPlatform((p) => ({
                            ...p,
                            usageNotifications: {
                              ...defaultHostedAIPlatform().usageNotifications,
                              ...p.usageNotifications,
                              notifyChannels: Array.from(cur),
                            },
                          }));
                        }}
                      />
                    }
                    label={ch === 'email_admin' ? 'Email admins (when server mail is configured)' : 'In-app banner'}
                  />
                ))}
              </FormGroup>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Operation costs (credit weights &amp; caps)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: '56rem' }}>
                Empty cells use the server default from the operation registry. Set weekly cap to <strong>0</strong> to
                disable the weekly limit for that operation.
              </Typography>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  overflowX: 'auto',
                  borderRadius: 1,
                }}
              >
                <Table size="medium" stickyHeader sx={{ minWidth: 880 }}>
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: 'action.hover', fontWeight: 700, py: 1.5 } }}>
                      <TableCell sx={{ minWidth: 200 }}>Operation</TableCell>
                      <TableCell align="right" sx={{ width: 108, whiteSpace: 'nowrap' }}>
                        <Tooltip title="Credit weight (empty = default)">
                          <span>Credits</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ width: 120, whiteSpace: 'nowrap' }}>
                        <Tooltip title="Max input tokens">
                          <span>Max in</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ width: 120, whiteSpace: 'nowrap' }}>
                        <Tooltip title="Max output tokens">
                          <span>Max out</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center" sx={{ width: 88 }}>
                        <Tooltip title="Allow in bulk jobs">
                          <span>Bulk</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ width: 120, whiteSpace: 'nowrap' }}>
                        <Tooltip title="Per company, per day">
                          <span>Day cap</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ width: 120, whiteSpace: 'nowrap' }}>
                        <Tooltip title="Per company, per week">
                          <span>Week cap</span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {HOSTED_OPERATION_ROWS.map(({ key, label }) => {
                      const oc = hostedAIPlatform.operationCosts?.[key] || {};
                      const def = HOSTED_OPERATION_DEFAULTS[key] || {};
                      return (
                        <TableRow
                          key={key}
                          sx={{
                            '&:nth-of-type(even)': { bgcolor: 'action.hover' },
                            '&:last-child td': { borderBottom: 0 },
                          }}
                        >
                          <TableCell sx={{ verticalAlign: 'middle', py: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'ui-monospace, monospace' }}>
                              {key}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1.5, verticalAlign: 'middle' }}>
                            <TextField
                              size="small"
                              type="number"
                              placeholder={placeholderForDefault(def.creditWeight)}
                              value={oc.creditWeight ?? ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                setHostedAIPlatform((p) => ({
                                  ...p,
                                  operationCosts: {
                                    ...(p.operationCosts || {}),
                                    [key]: {
                                      ...(p.operationCosts?.[key] || {}),
                                      creditWeight: v === '' ? undefined : parseInt(v, 10),
                                    },
                                  },
                                }));
                              }}
                              inputProps={{
                                'aria-label': `${label} credits`,
                                style: { textAlign: 'right' },
                              }}
                              sx={{ width: '100%', maxWidth: 96, '& input': { py: 1 } }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1.5, verticalAlign: 'middle' }}>
                            <TextField
                              size="small"
                              type="number"
                              placeholder={placeholderForDefault(def.maxInputTokens)}
                              value={oc.maxInputTokens ?? ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                setHostedAIPlatform((p) => ({
                                  ...p,
                                  operationCosts: {
                                    ...(p.operationCosts || {}),
                                    [key]: {
                                      ...(p.operationCosts?.[key] || {}),
                                      maxInputTokens: v === '' ? undefined : parseInt(v, 10),
                                    },
                                  },
                                }));
                              }}
                              inputProps={{
                                'aria-label': `${label} max input tokens`,
                                style: { textAlign: 'right' },
                              }}
                              sx={{ width: '100%', maxWidth: 108, '& input': { py: 1 } }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1.5, verticalAlign: 'middle' }}>
                            <TextField
                              size="small"
                              type="number"
                              placeholder={placeholderForDefault(def.maxOutputTokens)}
                              value={oc.maxOutputTokens ?? ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                setHostedAIPlatform((p) => ({
                                  ...p,
                                  operationCosts: {
                                    ...(p.operationCosts || {}),
                                    [key]: {
                                      ...(p.operationCosts?.[key] || {}),
                                      maxOutputTokens: v === '' ? undefined : parseInt(v, 10),
                                    },
                                  },
                                }));
                              }}
                              inputProps={{
                                'aria-label': `${label} max output tokens`,
                                style: { textAlign: 'right' },
                              }}
                              sx={{ width: '100%', maxWidth: 108, '& input': { py: 1 } }}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ py: 1.5, verticalAlign: 'middle' }}>
                            <Switch
                              size="small"
                              checked={oc.allowedInBulk ?? def.allowedInBulk ?? true}
                              onChange={(e) =>
                                setHostedAIPlatform((p) => ({
                                  ...p,
                                  operationCosts: {
                                    ...(p.operationCosts || {}),
                                    [key]: {
                                      ...(p.operationCosts?.[key] || {}),
                                      allowedInBulk: e.target.checked,
                                    },
                                  },
                                }))
                              }
                              inputProps={{ 'aria-label': `${label} allowed in bulk` }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1.5, verticalAlign: 'middle' }}>
                            <TextField
                              size="small"
                              type="number"
                              placeholder={placeholderForDefault(def.perDayCapPerCompany)}
                              value={oc.perDayCapPerCompany ?? ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                setHostedAIPlatform((p) => ({
                                  ...p,
                                  operationCosts: {
                                    ...(p.operationCosts || {}),
                                    [key]: {
                                      ...(p.operationCosts?.[key] || {}),
                                      perDayCapPerCompany: v === '' ? null : parseInt(v, 10),
                                    },
                                  },
                                }));
                              }}
                              inputProps={{
                                'aria-label': `${label} per day cap`,
                                style: { textAlign: 'right' },
                              }}
                              sx={{ width: '100%', maxWidth: 108, '& input': { py: 1 } }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1.5, verticalAlign: 'middle' }}>
                            <TextField
                              size="small"
                              type="number"
                              placeholder={placeholderForDefault(def.perWeekCapPerCompany)}
                              value={oc.perWeekCapPerCompany ?? ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                setHostedAIPlatform((p) => ({
                                  ...p,
                                  operationCosts: {
                                    ...(p.operationCosts || {}),
                                    [key]: {
                                      ...(p.operationCosts?.[key] || {}),
                                      perWeekCapPerCompany: v === '' ? null : parseInt(v, 10),
                                    },
                                  },
                                }));
                              }}
                              inputProps={{
                                'aria-label': `${label} per week cap`,
                                style: { textAlign: 'right' },
                              }}
                              sx={{ width: '100%', maxWidth: 108, '& input': { py: 1 } }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Customer AI (BYOK) — independent of Novo-hosted AI */}
      <Accordion
        expanded={customerAiSectionExpanded}
        onChange={(_, isExpanded) => setCustomerAiSectionExpanded(isExpanded)}
        id="bi-customer-ai"
        elevation={0}
        sx={{
          mb: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="bi-customer-ai-panel-content"
          id="bi-customer-ai-panel-header"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%', pr: 1 }}>
            <VpnKeyIcon color="primary" fontSize="medium" />
            <Typography variant="subtitle1" component="span" sx={{ fontWeight: 600 }}>
              Customer AI (Bring Your Own Key)
            </Typography>
            <Chip
              size="small"
              label={hostedAIPlatform?.customerAIEnabled === false ? 'OFF' : 'ON'}
              color={hostedAIPlatform?.customerAIEnabled === false ? 'default' : 'success'}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails id="bi-customer-ai-panel-content">
          <Stack spacing={3}>
            <Alert severity="info" sx={{ py: 1.5 }}>
              <Typography variant="body2" component="div">
                Controls whether customers may use <strong>their own</strong> OpenAI/Gemini API keys in the iOS app
                (Settings → AI API Keys and AI Write capsules). Independent of Novo-hosted AI above.
                When this master switch is off, the iOS app hides the AI API Keys page and hides AI Write
                capsules unless Novo-hosted AI is still enabled for that tier.
              </Typography>
            </Alert>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Master control
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={hostedAIPlatform.customerAIEnabled !== false}
                    onChange={(e) =>
                      setHostedAIPlatform((p) => ({ ...p, customerAIEnabled: e.target.checked }))
                    }
                  />
                }
                label="Customer AI enabled (own API keys)"
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                Per-tier BYOK
              </Typography>
              <Grid container spacing={2}>
                {['free', 'standard', 'professional', 'enterprise'].map((tier) => (
                  <Grid item xs={12} sm={6} md={3} key={`byok-${tier}`}>
                    <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, textTransform: 'capitalize', fontWeight: 600 }}>
                        {tier} tier
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            disabled={hostedAIPlatform.customerAIEnabled === false}
                            checked={hostedAIPlatform.tiers?.[tier]?.byokEnabled !== false}
                            onChange={(e) =>
                              setHostedAIPlatform((p) => ({
                                ...p,
                                tiers: {
                                  ...defaultHostedAIPlatform().tiers,
                                  ...p.tiers,
                                  [tier]: {
                                    ...defaultHostedAIPlatform().tiers[tier],
                                    ...p.tiers?.[tier],
                                    byokEnabled: e.target.checked,
                                  },
                                },
                              }))
                            }
                          />
                        }
                        label="BYOK enabled"
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Novo Voice — subscription feature flags (separate from hosted-AI operation costs above) */}
      <Accordion
        defaultExpanded={false}
        id="novo-desk-feature"
        sx={{
          mb: 1,
          border: '1px solid',
          borderColor: 'secondary.main',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%', pr: 1 }}>
            <MicIcon color="secondary" fontSize="medium" />
            <Typography variant="subtitle1" component="span" sx={{ fontWeight: 600 }}>
              Novo Voice
            </Typography>
            <Chip
              size="small"
              label={`Voice AI ${featurePricing?.voiceAssistant?.enabled === false ? 'OFF' : 'ON'}`}
              color={featurePricing?.voiceAssistant?.enabled === false ? 'default' : 'success'}
            />
            <Chip
              size="small"
              label={`Feature Setup ${featurePricing?.voiceFeatureSetup?.enabled === true ? 'ON' : 'OFF'}`}
              color={featurePricing?.voiceFeatureSetup?.enabled === true ? 'success' : 'default'}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Alert severity="info">
              <Typography variant="body2" component="div">
                <strong>Novo Voice (Voice AI)</strong> controls whether the Hi Novo microphone and voice commands appear in the iOS app per subscription tier.
                It is <strong>not</strong> the same as <em>Voice command parse</em> / <em>Voice command clarify</em> in the Hosted AI operation-cost table above —
                those rows only define hosted-AI credit weights for future server-side parsing.
              </Typography>
              <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                <strong>Novo Voice Feature Setup</strong> is a separate kill switch for conversational appointment setup.
                It stays off unless you enable it here. Turning Novo Voice on never enables Feature Setup.
              </Typography>
            </Alert>
            {[{
              key: 'voiceAssistant',
              fallback: {
                name: 'Novo Voice (Voice AI)',
                description: 'In-app voice commands via Hi Novo — disabled by default until rollout',
              },
              emptyConfig: { enabled: false, price: 0, availableInTiers: [] },
            }, {
              key: 'voiceFeatureSetup',
              fallback: {
                name: 'Novo Voice Feature Setup',
                description: 'Conversational setup that configures features after confirmation. Off by default and never cascades from Novo Voice.',
              },
              emptyConfig: { ...VOICE_FEATURE_SETUP_DEFAULT },
            }].map(({ key, fallback, emptyConfig }) => {
              const info = features[key] || fallback;
              const config = featurePricing[key] || emptyConfig;

              return (
                <Card variant="outlined" key={key}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="subtitle1">{info.name}</Typography>
                        <Typography variant="body2" color="text.secondary">{info.description}</Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`feature: ${key}`}
                          sx={{ mt: 0.75, fontFamily: 'ui-monospace, monospace' }}
                        />
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={config.enabled === true}
                            onChange={(e) => handleFeatureToggle(key, 'enabled', e.target.checked)}
                          />
                        }
                        label={config.enabled === true ? 'ENABLED' : 'DISABLED'}
                      />
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                        Tier access:
                      </Typography>
                      <FormGroup row>
                        {['free', 'standard', 'professional', 'enterprise'].map((tier) => {
                          const isTierChecked = config.availableInTiers?.includes(tier);
                          return (
                            <FormControlLabel
                              key={tier}
                              control={
                                <Checkbox
                                  checked={isTierChecked}
                                  onChange={() => handleTierToggle(key, tier)}
                                  disabled={!config.enabled}
                                  size="small"
                                />
                              }
                              label={
                                <Chip
                                  label={tier.charAt(0).toUpperCase() + tier.slice(1)}
                                  size="small"
                                  variant={isTierChecked ? 'filled' : 'outlined'}
                                />
                              }
                            />
                          );
                        })}
                      </FormGroup>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Product Limits */}
      <Accordion defaultExpanded={false} sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Product Limits per Tier</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Controls how many products/services a company can create in the iOS app per subscription tier.
          </Typography>
          <Grid container spacing={3}>
            {['free', 'standard', 'professional', 'enterprise'].map(tier => (
              <Grid item xs={12} md={4} key={tier}>
                <TextField
                  fullWidth
                  label={`${tier.charAt(0).toUpperCase() + tier.slice(1)} - Max Products`}
                  type="number"
                  value={tierPricing[tier]?.features?.maxProducts ?? ''}
                  onChange={(e) => handleProductLimitChange(tier, e.target.value)}
                  helperText={tier === 'free' ? 'Required for free tier' : 'Leave empty for unlimited'}
                />
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Multi-Company Global Kill Switch */}
      <Accordion defaultExpanded={false} sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">Multi-Company (Global Kill Switch)</Typography>
            <Chip
              label={featureFlags?.enableMultiCompany === false ? 'DISABLED' : 'ENABLED'}
              size="small"
              color={featureFlags?.enableMultiCompany === false ? 'error' : 'success'}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            When OFF, the iOS app will hide all multi-company UI (company management, add company, switching) for all tiers.
            This does not delete any existing company data. Individual iCloud accounts can still be granted a
            <strong> Multi-Company Feature Override</strong> from Company Details so you can test with a specific
            account while this kill switch stays OFF.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={featureFlags?.enableMultiCompany !== false}
                onChange={(e) => handleMultiCompanyGlobalToggle(e.target.checked)}
                color="success"
              />
            }
            label={featureFlags?.enableMultiCompany === false ? 'DISABLED' : 'ENABLED'}
            labelPlacement="start"
          />
        </AccordionDetails>
      </Accordion>

      <Accordion
        defaultExpanded={false}
        sx={{ mb: 1, border: '1px solid', borderColor: 'primary.light', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">Setup Consultant (Global Kill Switch)</Typography>
            <Chip
              label={featureFlags?.enableSetupConsultant !== false ? 'ENABLED' : 'DISABLED'}
              size="small"
              color={featureFlags?.enableSetupConsultant !== false ? 'success' : 'error'}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Shows the AI Setup Consultant during Simple setup (after company details), after Advanced
            setup if chosen, and in Settings. This is the switch that makes the interview appear.
            The Hosted AI <strong>Interview</strong> checkbox only lets Novo-hosted AI run that
            interview; it does not show the screens.
          </Typography>
          {featureFlags?.enableSetupConsultant === false && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Setup Consultant is currently <strong>HIDDEN</strong> on every iOS client, including first-run.
            </Alert>
          )}
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={featureFlags?.enableSetupConsultant !== false}
                  onChange={(e) => handleSetupConsultantToggle(e.target.checked)}
                  color="success"
                />
              }
              label={
                featureFlags?.enableSetupConsultant !== false
                  ? 'ENABLED — interview is offered during onboarding'
                  : 'DISABLED — hidden from users'
              }
              labelPlacement="start"
            />
            <TextField
              fullWidth
              label="Pilot tenant IDs (optional)"
              value={(featureFlags?.setupConsultantPilotTenants || []).join(', ')}
              onChange={(e) => handleSetupConsultantPilotTenantsChange(e.target.value)}
              helperText="Comma-separated company IDs. Empty means every tenant."
            />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2">Pilot business types (optional)</Typography>
                <Button size="small" onClick={() => handleSetupConsultantPilotBusinessTypesSelectAll(true)}>
                  All
                </Button>
                <Button size="small" onClick={() => handleSetupConsultantPilotBusinessTypesSelectAll(false)}>
                  None
                </Button>
              </Box>
              <FormGroup row>
                {ALL_BUSINESS_TYPES.map((bt) => (
                  <FormControlLabel
                    key={`setup-consultant-bt-${bt.value}`}
                    control={
                      <Checkbox
                        size="small"
                        checked={(featureFlags?.setupConsultantPilotBusinessTypes || []).includes(bt.value)}
                        onChange={(e) =>
                          handleSetupConsultantPilotBusinessTypeToggle(bt.value, e.target.checked)
                        }
                      />
                    }
                    label={bt.label}
                  />
                ))}
              </FormGroup>
              <Typography variant="caption" color="text.secondary">
                Empty means every business type. Checking any type restricts the interview to those types.
              </Typography>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Alert severity="info" sx={{ mt: 2, mb: 1 }}>
        Inventory and order-entry rollout controls are grouped below. Quick Create controls inventory item creation;
        Visual Quick-Add controls POS, dashboard Quick-Add, and the New Invoice visual entry mode.
      </Alert>

      {/* Quick Create Global Kill Switch */}
      <Accordion
        defaultExpanded={false}
        sx={{ mb: 1, border: '1px solid', borderColor: 'info.light', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">Quick Create (Global Kill Switch)</Typography>
            <Chip
              label={featureFlags?.enableQuickCreate === true ? 'ENABLED' : 'DISABLED'}
              size="small"
              color={featureFlags?.enableQuickCreate === true ? 'success' : 'error'}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Controls the iOS Inventory Quick Create surface. This is fail-closed by default and can be staged by tenant IDs or business-type raw values.
          </Typography>
          {featureFlags?.enableQuickCreate !== true && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Quick Create is currently <strong>HIDDEN</strong> for all iOS clients.
            </Alert>
          )}
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={featureFlags?.enableQuickCreate === true}
                  onChange={(e) => handleQuickCreateToggle(e.target.checked)}
                  color="success"
                />
              }
              label={featureFlags?.enableQuickCreate === true ? 'ENABLED — pilots may see Quick Create' : 'DISABLED — hidden from users'}
              labelPlacement="start"
            />
            <TextField
              fullWidth
              label="Pilot Tenant IDs"
              value={(featureFlags?.quickCreatePilotTenants || []).join(', ')}
              onChange={(e) => handleQuickCreatePilotTenantsChange(e.target.value)}
              helperText="Comma-separated company IDs. Leave empty for no tenant restriction."
            />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Pilot Business Types</Typography>
                <Box>
                  <Button size="small" onClick={() => handleQuickCreatePilotBusinessTypesSelectAll(true)} sx={{ mr: 1 }}>
                    Select All
                  </Button>
                  <Button size="small" onClick={() => handleQuickCreatePilotBusinessTypesSelectAll(false)}>
                    Deselect All
                  </Button>
                </Box>
              </Box>
              <FormGroup row>
                {ALL_BUSINESS_TYPES.map(bt => (
                  <FormControlLabel
                    key={bt.value}
                    control={
                      <Checkbox
                        size="small"
                        checked={(featureFlags?.quickCreatePilotBusinessTypes || []).includes(bt.value)}
                        onChange={(e) => handleQuickCreatePilotBusinessTypeToggle(bt.value, e.target.checked)}
                      />
                    }
                    label={bt.label}
                    sx={{ width: '50%', m: 0 }}
                  />
                ))}
              </FormGroup>
              <Typography variant="caption" color="text.secondary">
                Leave all unchecked for no business-type restriction.
              </Typography>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Visual Quick-Add Global Kill Switch */}
      <Accordion
        defaultExpanded={false}
        sx={{ mb: 1, border: '1px solid', borderColor: 'info.light', '&:before': { display: 'none' } }}
      >
        <AccordionSummary id="visual-quick-add-global-kill-switch" expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">Visual Quick-Add (Global Kill Switch)</Typography>
            <Chip
              label={featureFlags?.enableVisualQuickAdd === true ? 'ENABLED' : 'DISABLED'}
              size="small"
              color={featureFlags?.enableVisualQuickAdd === true ? 'success' : 'error'}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Controls the iOS Visual Quick-Add surfaces (POS visual grid, dashboard Quick-Add tab, and the New Invoice visual entry mode). Fail-closed by default; stage rollouts by tenant ID or business-type raw values.
          </Typography>
          {featureFlags?.enableVisualQuickAdd !== true && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Visual Quick-Add is currently <strong>HIDDEN</strong> for all iOS clients.
            </Alert>
          )}
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={featureFlags?.enableVisualQuickAdd === true}
                  onChange={(e) => handleVisualQuickAddToggle(e.target.checked)}
                  color="success"
                />
              }
              label={featureFlags?.enableVisualQuickAdd === true ? 'ENABLED — pilots may see Visual Quick-Add' : 'DISABLED — hidden from users'}
              labelPlacement="start"
            />
            <TextField
              fullWidth
              label="Pilot Tenant IDs"
              value={(featureFlags?.visualQuickAddPilotTenants || []).join(', ')}
              onChange={(e) => handleVisualQuickAddPilotTenantsChange(e.target.value)}
              helperText="Comma-separated company IDs. Leave empty for no tenant restriction."
            />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Pilot Business Types</Typography>
                <Box>
                  <Button size="small" onClick={() => handleVisualQuickAddPilotBusinessTypesSelectAll(true)} sx={{ mr: 1 }}>
                    Select All
                  </Button>
                  <Button size="small" onClick={() => handleVisualQuickAddPilotBusinessTypesSelectAll(false)}>
                    Deselect All
                  </Button>
                </Box>
              </Box>
              <FormGroup row>
                {ALL_BUSINESS_TYPES.map(bt => (
                  <FormControlLabel
                    key={bt.value}
                    control={
                      <Checkbox
                        size="small"
                        checked={(featureFlags?.visualQuickAddPilotBusinessTypes || []).includes(bt.value)}
                        onChange={(e) => handleVisualQuickAddPilotBusinessTypeToggle(bt.value, e.target.checked)}
                      />
                    }
                    label={bt.label}
                    sx={{ width: '50%', m: 0 }}
                  />
                ))}
              </FormGroup>
              <Typography variant="caption" color="text.secondary">
                Leave all unchecked for no business-type restriction.
              </Typography>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Novo Market Global Kill Switch */}
      <Accordion defaultExpanded={false} sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">Novo Market (Global Kill Switch)</Typography>
            <Chip
              label={featureFlags?.enableNovoMarket === false ? 'DISABLED' : 'ENABLED'}
              size="small"
              color={featureFlags?.enableNovoMarket === false ? 'error' : 'success'}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            When OFF, the iOS app runs in business-owner mode only: no Customer vs Business choice at first install,
            Novo Market Manager is hidden from the dashboard and Settings, and the switch-to-customer option is removed.
          </Typography>
          {featureFlags?.enableNovoMarket === false && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Novo Market is currently <strong>DISABLED</strong> — iOS users only see the business app.
            </Alert>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={featureFlags?.enableNovoMarket !== false}
                onChange={(e) => handleNovoMarketToggle(e.target.checked)}
                color="success"
              />
            }
            label={featureFlags?.enableNovoMarket === false ? 'DISABLED — business app only' : 'ENABLED — customer mode available'}
            labelPlacement="start"
          />
        </AccordionDetails>
      </Accordion>

      {/* AI Marketing Global Kill Switch */}
      <Accordion defaultExpanded={false} sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">AI Marketing (Global Kill Switch)</Typography>
            <Chip
              label={featureFlags?.enableAIMarketing === true ? 'ENABLED' : 'DISABLED'}
              size="small"
              color={featureFlags?.enableAIMarketing === true ? 'success' : 'error'}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            When OFF, the "AI Marketing" tile is hidden from Website Integration settings in the iOS app for all users.
            Use this to temporarily disable the tile while AI marketing features are being repaired.
            Set to ON once the features are fixed and ready.
          </Typography>
          {featureFlags?.enableAIMarketing !== true && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              AI Marketing tile is currently <strong>HIDDEN</strong> from users.
            </Alert>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={featureFlags?.enableAIMarketing === true}
                onChange={(e) => handleAIMarketingToggle(e.target.checked)}
                color="success"
              />
            }
            label={featureFlags?.enableAIMarketing === true ? 'ENABLED — tile is visible to users' : 'DISABLED — tile is hidden from users'}
            labelPlacement="start"
          />
        </AccordionDetails>
      </Accordion>

      {/* Company Limits */}
      <Accordion defaultExpanded={false} sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Company Limits per Tier</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Controls how many companies a user can manage in the iOS app for each subscription tier.
            Use <strong>1</strong> to effectively disable multi-company for a tier. Leave empty for unlimited.
          </Typography>
          <Grid container spacing={3}>
            {['free', 'standard', 'professional', 'enterprise'].map(tier => (
              <Grid item xs={12} md={4} key={tier}>
                <TextField
                  fullWidth
                  label={`${tier.charAt(0).toUpperCase() + tier.slice(1)} - Max Companies`}
                  type="number"
                  value={tierPricing[tier]?.features?.maxCompanies ?? ''}
                  onChange={(e) => handleCompanyLimitChange(tier, e.target.value)}
                  helperText={tier === 'free' ? 'Must be at least 1' : 'Use 1 to disable, empty = unlimited'}
                />
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Website Product Upload Limits */}
      <Accordion defaultExpanded={false} sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Website Product Upload Limits per Tier</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Controls how many products each subscription tier can upload/sync to their website (separate from app product creation limit).
            Limits are disabled for tiers that do not have Website Access enabled.
          </Typography>
          <Grid container spacing={3}>
            {['free', 'standard', 'professional', 'enterprise'].map(tier => {
              const websiteAccessEnabled = isTierWebsiteAccessEnabled(tier);
              return (
                <Grid item xs={12} md={4} key={tier}>
                  <Tooltip
                    title={!websiteAccessEnabled ? `Website Access is not enabled for the ${tier.charAt(0).toUpperCase() + tier.slice(1)} tier` : ''}
                    placement="top"
                  >
                    <span>
                      <TextField
                        fullWidth
                        label={`${tier.charAt(0).toUpperCase() + tier.slice(1)} - Max Website Products`}
                        type="number"
                        value={tierPricing[tier]?.features?.maxWebsiteProducts ?? ''}
                        onChange={(e) => handleWebsiteProductLimitChange(tier, e.target.value)}
                        disabled={!websiteAccessEnabled}
                        helperText={
                          !websiteAccessEnabled
                            ? 'Requires Website Access to be enabled for this tier'
                            : tier === 'free' ? 'Required for free tier' : 'Leave empty for unlimited'
                        }
                        sx={!websiteAccessEnabled ? { '& .MuiInputBase-root': { bgcolor: 'action.disabledBackground' } } : {}}
                      />
                    </span>
                  </Tooltip>
                </Grid>
              );
            })}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Language Selection */}
      <Accordion defaultExpanded={false} sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">🌍 Enabled Languages</Typography>
            <Chip
              label={!featureFlags?.enabledLanguages?.length ? 'All Enabled' : `${featureFlags.enabledLanguages.length} selected`}
              size="small"
              color="info"
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select which languages are available in the iOS app and website. When no languages are selected, all languages are enabled (default).
          </Typography>
          <Grid container spacing={1}>
            {[
              { code: 'en', name: 'English', flag: '🇺🇸' },
              { code: 'es', name: 'Spanish', flag: '🇪🇸' },
              { code: 'fr', name: 'French', flag: '🇫🇷' },
              { code: 'de', name: 'German', flag: '🇩🇪' },
              { code: 'it', name: 'Italian', flag: '🇮🇹' },
              { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
              { code: 'zh-Hans', name: 'Chinese (Simplified)', flag: '🇨🇳' },
              { code: 'zh-Hant', name: 'Chinese (Traditional)', flag: '🇹🇼' },
              { code: 'zh-HK', name: 'Chinese (Hong Kong)', flag: '🇭🇰' },
              { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
              { code: 'ko', name: 'Korean', flag: '🇰🇷' },
              { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
              { code: 'ru', name: 'Russian', flag: '🇷🇺' },
              { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
              { code: 'fa', name: 'Persian (Farsi)', flag: '🇮🇷' },
              { code: 'tr', name: 'Turkish', flag: '🇹🇷' },
              { code: 'vi', name: 'Vietnamese', flag: '🇻🇳' },
              { code: 'id', name: 'Indonesian', flag: '🇮🇩' },
              { code: 'th', name: 'Thai', flag: '🇹🇭' },
              { code: 'nl', name: 'Dutch', flag: '🇳🇱' },
              { code: 'el', name: 'Greek', flag: '🇬🇷' },
              { code: 'ro', name: 'Romanian', flag: '🇷🇴' },
              { code: 'he', name: 'Hebrew', flag: '🇮🇱' },
              { code: 'pl', name: 'Polish', flag: '🇵🇱' },
              { code: 'uk', name: 'Ukrainian', flag: '🇺🇦' },
              { code: 'ms', name: 'Malay', flag: '🇲🇾' },
              { code: 'bn', name: 'Bengali', flag: '🇧🇩' }
            ].map(lang => {
              const isEnabled = !featureFlags?.enabledLanguages?.length || featureFlags.enabledLanguages.includes(lang.code);
              return (
                <Grid item xs={6} sm={4} md={3} key={lang.code}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isEnabled}
                        onChange={(e) => {
                          const currentEnabled = featureFlags?.enabledLanguages || [];
                          let newEnabled;
                          if (e.target.checked) {
                            if (currentEnabled.length === 0) {
                              newEnabled = [];
                            } else {
                              newEnabled = [...currentEnabled, lang.code];
                            }
                          } else {
                            if (currentEnabled.length === 0) {
                              const allLangs = ['en', 'es', 'fr', 'de', 'it', 'pt', 'zh-Hans', 'zh-Hant', 'zh-HK', 'ja', 'ko', 'ar', 'ru', 'hi', 'fa', 'tr', 'vi', 'id', 'th', 'nl', 'el', 'ro', 'he', 'pl', 'uk', 'ms', 'bn'];
                              newEnabled = allLangs.filter(l => l !== lang.code);
                            } else {
                              newEnabled = currentEnabled.filter(l => l !== lang.code);
                            }
                          }
                          setFeatureFlags(prev => ({ ...prev, enabledLanguages: newEnabled }));
                        }}
                        size="small"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <span>{lang.flag}</span>
                        <Typography variant="body2">{lang.name}</Typography>
                      </Box>
                    }
                  />
                </Grid>
              );
            })}
          </Grid>
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setFeatureFlags(prev => ({ ...prev, enabledLanguages: [] }))}
            >
              Enable All
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setFeatureFlags(prev => ({ ...prev, enabledLanguages: ['en'] }))}
            >
              English Only
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Tax API Provider Tier Availability */}
      <Accordion defaultExpanded={false} sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Tax API Provider Availability per Tier</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Controls which Tax API Providers (TaxJar, Avalara, Stripe Tax) are available to users in each subscription tier.
            Disabling a provider hides it from the Tax API Provider settings page in the iOS app.
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(taxAPIProviderDefinitions).map(([providerKey, providerDef]) => {
              const config = taxProviderAvailability[providerKey] || { enabled: true, availableInTiers: [...providerDef.defaultTiers] };
              return (
                <Grid item xs={12} key={providerKey}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="subtitle1">
                            {providerDef.icon} {providerDef.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">{providerDef.description}</Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={config.enabled}
                              onChange={(e) => {
                                const enabled = e.target.checked;
                                setTaxProviderAvailability(prev => ({
                                  ...prev,
                                  [providerKey]: {
                                    ...prev[providerKey],
                                    enabled,
                                    availableInTiers: enabled ? [...providerDef.defaultTiers] : []
                                  }
                                }));
                              }}
                              color="success"
                            />
                          }
                          label={config.enabled ? 'Enabled' : 'Disabled'}
                          labelPlacement="start"
                        />
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                          Tier Access:
                        </Typography>
                        <FormGroup row>
                          {['free', 'standard', 'professional', 'enterprise'].map(tier => {
                            const isTierChecked = config.availableInTiers?.includes(tier);
                            return (
                              <FormControlLabel
                                key={tier}
                                control={
                                  <Checkbox
                                    checked={isTierChecked}
                                    onChange={() => {
                                      setTaxProviderAvailability(prev => {
                                        const current = prev[providerKey]?.availableInTiers || [];
                                        const newTiers = isTierChecked
                                          ? current.filter(t => t !== tier)
                                          : [...current, tier];
                                        return {
                                          ...prev,
                                          [providerKey]: { ...prev[providerKey], availableInTiers: newTiers }
                                        };
                                      });
                                    }}
                                    disabled={!config.enabled}
                                    size="small"
                                  />
                                }
                                label={
                                  <Chip
                                    label={tier.charAt(0).toUpperCase() + tier.slice(1)}
                                    size="small"
                                    color={tier === 'free' ? 'default' : tier === 'standard' ? 'info' : tier === 'professional' ? 'primary' : 'secondary'}
                                    variant={isTierChecked ? 'filled' : 'outlined'}
                                  />
                                }
                              />
                            );
                          })}
                        </FormGroup>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Features organized by category */}
      {Object.entries(featureCategories).map(([categoryName, categoryFeatures]) => {
        const featureKeys = Object.keys(categoryFeatures);
        const enabledCount = featureKeys.filter(key => featurePricing[key]?.enabled).length;
        const isTipManagementCategory = categoryName === 'Tip Management & Shifts';

        return (
          <Accordion key={categoryName} defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Typography variant="h6">{categoryName}</Typography>
                {isTipManagementCategory && (
                  <Chip 
                    icon={<SyncIcon sx={{ fontSize: 14 }} />}
                    label="Synced with Tip Management tab" 
                    size="small" 
                    color="info" 
                    variant="outlined"
                  />
                )}
                <Chip
                  label={`${enabledCount}/${featureKeys.length} enabled`}
                  size="small"
                  color={enabledCount === featureKeys.length ? 'success' : enabledCount > 0 ? 'warning' : 'default'}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {/* Global toggle for Tip Management category */}
              {isTipManagementCategory && (
                <Paper sx={{ p: 2, mb: 2, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        💰 Global Tip Management Control
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Master switch for all tip management features. When OFF, all features below are disabled.
                        This setting is synced with the dedicated Tip Management tab.
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch 
                          checked={tipManagementGlobalEnabled} 
                          onChange={(e) => handleTipManagementGlobalToggle(e.target.checked)}
                          color="success"
                          size="medium"
                        />
                      }
                      label={tipManagementGlobalEnabled ? 'ENABLED' : 'DISABLED'}
                      labelPlacement="start"
                    />
                  </Box>
                </Paper>
              )}
              
              <Grid container spacing={2} sx={{ opacity: isTipManagementCategory && !tipManagementGlobalEnabled ? 0.5 : 1 }}>
                {Object.entries(categoryFeatures).map(([key, info]) => {
                  const config = featurePricing[key] || (key === 'voiceFeatureSetup'
                    ? { ...VOICE_FEATURE_SETUP_DEFAULT }
                    : { enabled: true, price: 0, availableInTiers: ['free', 'standard', 'professional', 'enterprise'] });
                  const isDisabledByGlobal = isTipManagementCategory && !tipManagementGlobalEnabled;
                  const isWebsiteDependent = websiteAccessDependentFeatureKeys.includes(key);
                  const websiteAccessFullyDisabled = !featurePricing['websiteAccess']?.enabled;
                  const isDisabledByWebsiteAccess = isWebsiteDependent && websiteAccessFullyDisabled;
                  const isTierNeutralFeature = TIER_NEUTRAL_FEATURE_KEYS.has(key);

                  return (
                    <Grid item xs={12} key={key}>
                      <Card variant="outlined" sx={isDisabledByWebsiteAccess ? { opacity: 0.6 } : {}}>
                        <CardContent sx={{ py: 1.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                              <Typography variant="subtitle1">
                                {info.name}
                                {isWebsiteDependent && (
                                  <Chip
                                    label="Requires Website Access"
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    sx={{ ml: 1, fontSize: '0.65rem', height: 20 }}
                                  />
                                )}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">{info.description}</Typography>
                              {isDisabledByWebsiteAccess && (
                                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                  ⚠️ This feature requires Website Access to be enabled
                                </Typography>
                              )}
                            </Box>
                            <Tooltip title={isDisabledByWebsiteAccess ? 'This feature requires Website Access to be enabled' : ''}>
                              <span>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={config.enabled}
                                      onChange={(e) => handleFeatureToggle(key, 'enabled', e.target.checked)}
                                      disabled={isDisabledByGlobal || isDisabledByWebsiteAccess}
                                    />
                                  }
                                  label="Enabled"
                                />
                              </span>
                            </Tooltip>
                          </Box>
                          <Divider sx={{ my: 1 }} />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                              Tier Access:
                            </Typography>
                            <FormGroup row>
                              {['free', 'standard', 'professional', 'enterprise'].map(tier => {
                                const tierBlockedByWebsite = isWebsiteDependent && !isTierWebsiteAccessEnabled(tier);
                                const isCheckDisabled = !config.enabled || isDisabledByGlobal || tierBlockedByWebsite || isTierNeutralFeature;

                                const isTierChecked = isTierNeutralFeature && config.enabled
                                  ? true
                                  : config.availableInTiers?.includes(tier);
                                return (
                                  <Box key={tier} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Tooltip
                                      title={tierBlockedByWebsite ? `This feature requires Website Access to be enabled for the ${tier.charAt(0).toUpperCase() + tier.slice(1)} tier` : ''}
                                    >
                                      <span>
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              checked={isTierChecked}
                                              onChange={() => handleTierToggle(key, tier)}
                                              disabled={isCheckDisabled}
                                              size="small"
                                            />
                                          }
                                          label={
                                            <Chip
                                              label={tier.charAt(0).toUpperCase() + tier.slice(1)}
                                              size="small"
                                              color={tier === 'free' ? 'default' : tier === 'professional' ? 'primary' : 'secondary'}
                                              variant={isTierChecked ? 'filled' : 'outlined'}
                                            />
                                          }
                                        />
                                      </span>
                                    </Tooltip>
                                    {/* Max Branches input - only for multiLocationSupport, only when tier is checked */}
                                    {key === 'multiLocationSupport' && isTierChecked && (
                                      <TextField
                                        size="small"
                                        type="number"
                                        label="Max Branches"
                                        value={config.maxBranchesPerTier?.[tier] ?? ''}
                                        onChange={(e) => handleMaxBranchesChange(tier, e.target.value)}
                                        sx={{ width: 110, mr: 1 }}
                                        inputProps={{ min: 1 }}
                                        helperText="Required"
                                      />
                                    )}
                                  </Box>
                                );
                              })}
                            </FormGroup>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {createPortal(
        <Box
          component="aside"
          aria-label="Save features and limits"
          sx={{
            position: 'fixed',
            zIndex: (theme) => theme.zIndex.appBar,
            bottom: 0,
            // Match Layout.js drawerWidth so the bar sits in the main column on desktop.
            left: { xs: 0, md: 240 },
            right: 0,
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end',
            alignItems: 'center',
            flexWrap: 'wrap',
            px: { xs: 1.5, sm: 2, md: 3 },
            pt: 1.5,
            pb: 'calc(12px + env(safe-area-inset-bottom, 0px))',
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.08)',
          }}
        >
          {(error || success) && (
            <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 220 }, maxHeight: 80, overflow: 'auto' }}>
              {error && <Alert severity="error" sx={{ py: 0 }} onClose={() => setError(null)}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ py: 0 }} onClose={() => setSuccess(null)}>{success}</Alert>}
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', ml: 'auto' }}>
            <Button variant="outlined" startIcon={<RestoreIcon />} onClick={fetchConfiguration} disabled={saving}>Reset</Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
          </Box>
        </Box>,
        document.body
      )}
    </Box>
  );
}

